// Budapest, "Snow over the Danube". A December night of light, slow snow, seen from the Buda half of Margaret Bridge
// (Margit hid), about a third of the way across from the Buda end, a little above the deck and looking south-south-east
// over the Pest-bound tram track and the bridge's southern footway, down the Danube.
// What is really there, left to right: a Pest riverside block with a rooftop sign (the city's billboard), the dome of
// St Stephen's Basilica just showing over the roofs, the Hungarian Parliament at an angle (its north end, then its
// river front running away from us, the dome with the spires of both fronts and of the two chambers around it), the
// far Pest embankment, the Chain Bridge with Gellert Hill and the Liberty Statue behind it, the Royal Palace on the
// south end of Castle Hill, the Calvinist church and St Anne's on the Buda bank, Matthias Church and the Fisherman's
// Bastion on the hill, and the houses of Bem rakpart at the right edge. The Parliament is drawn about twice as large
// as the rest (a long lens on the left, a wide one on the right), but nothing is moved out of its real order.
// Not in view: Heroes' Square, the Szechenyi baths and Margaret Island (behind us), the Elisabeth Bridge (hidden behind
// the Chain Bridge but for its pylon tops).
// Back to front: sky, hazy moon, snow clouds -> far snow -> the river (mirror of both banks, ripples, lamp streaks)
// -> Gellert Hill, the Chain Bridge, Castle Hill and the Buda bank -> the Pest block and its roof sign -> the
// Parliament, its gulls circling in the floodlights -> boats, river mist, mid snow -> the bridge: railing, lamp posts,
// snowy footway and its few people, the chestnut cart -> the tram on the near track, the overhead wire -> near snow.
// render(t, { crowd }) thins the people on the footway and in the trams; crowd 0 draws nobody at all (see CROWD).
//
// The loop's timeline (seconds; every period divides 240):
//   event                                         time                     sound
//   yellow Combino tram, line 6 (right to left)   6 - 20                   train at 6; a spark off the wire at 11.5
//   the photographer bends to his camera          every 40 s (10, 50 ...)  8 s each
//   he points something out to her                14, 62, 110, 158, 228    6 s each
//   someone with a shopping bag walks by          22 - 52, back 140 - 170
//   tram 2 along the Pest quay                    28 - 52, 70 - 94, 132 - 156, 214 - 238
//   gulls call over the dome                      30, 200                  gull (they circle all loop long)
//   sightseeing boat turns across the river       38 - 98                  boat at 40
//   chestnuts handed over at the cart             every 60 s (44, 104 ...) crowd above 0.65 (the customer)
//   walker in a long coat and scarf               56 - 86, back 176 - 206
//   a couple arm in arm                           66 - 100, back 186 - 220 crowd above 0.7
//   yellow Combino tram, line 4                   86 - 100                 train at 86; a spark at 91.5
//   cyclist pushing a bike through the snow       100 - 132                crowd above 0.55
//   barge coming up the Buda side, under us       104 - 176
//   someone with a dog                            118 - 150, back 206 - 236 crowd above 0.9
//   Matthias Church strikes three                 122, 125, 128            bell; the gulls widen their circle until 156
//   the light tram (fenyvillamos) glides by       164 - 182                chime at 164
//   the sightseeing boat comes back               192 - 234                boat at 194
//   a bridge lamp sputters                        201 - 202.4
// The three bigger, gentle moments: the boat (first third), the bells and the gulls (middle), the light tram (last third).
// Longest gap between two starts at the default crowd: 18 s (140 -> 158, 228 -> 6).
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, cycle, scroll,
  hash, rng, bayer, hex, makeCanvas, glyph, FONT3, makeHalo, drawHalo, streak, makePitch } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                            // the scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
const YH = 150;                                     // eye level: far things meet the water just under it
const RIV0 = 154, RIV1 = 222;                       // rows the river is painted in (the land covers its top edge)
const RAIL = 207;                                   // the bridge's handrail
const WALK = 220;                                   // far edge of the footway, at the foot of the railing
const KERB = 240;                                   // kerb between the footway and the track
const TRAM_TOP = 220, TRAM_H = 48;                  // the tram on the near track: roof row and height
const WIRE = 188;                                   // the overhead contact wire
const LAMPS = [52, 308];                            // the bridge's lamp posts, on the railing line
const LAMP_Y = 163;                                 // their centre lantern
const BILL = { x: 30, y: 79, w: 42, h: 19 };        // the roof sign's face on the Pest block (the city's billboard)
const BILL_LAMPS = [BILL.x + 6, BILL.x + 21, BILL.x + 35], BILL_LAMP_Y = BILL.y - 5;
const MOON = { x: 368, y: 42 };
const DOME_X = 189;                                 // the Parliament's dome (see the projection below)
const CART = { x: 398, y: 234 };                    // the chestnut cart: left end, wheels' row
// the waterline, column by column: the Pest quay runs away from us on the left, the Buda quay on the right, and the
// river goes on under the Chain Bridge between them
const BANK_PTS = [[-1, 173], [28, 172], [112, 170], [179, 168], [306, 164], [316, 158], [320, 156], [376, 156], [384, 158],
  [420, 164], [452, 170], [481, 177]];
const BANKY = new Int16Array(W);
for (let i = 0, k = 0; i < W; i++) {
  while (BANK_PTS[k + 1][0] < i) k++;
  const [x0, y0] = BANK_PTS[k], [x1, y1] = BANK_PTS[k + 1];
  BANKY[i] = Math.round(lerp(y0, y1, (i - x0) / (x1 - x0)));
}

// The share of the people the scene shows by default: 1 = everyone, 0.5 = about half. render(t, { crowd }) overrides
// it for one frame; crowd 0 is the visitors' "None": nobody on the footway, nobody in the trams but their drivers,
// no tripod, bike or dog, and the chestnut cart stands unattended under its lamp, still steaming.
const CROWD = 0.5;
const nobody = crowd => crowd === 0;
const here = (rank, crowd) => !nobody(crowd) && (rank === 0 || rank < crowd);   // rank 0: the cast that stays while anybody does

/* ================= Timed events (loop seconds) ================= */
const TRAMS = [{ t0: 6, t1: 20, light: false }, { t0: 86, t1: 100, light: false }, { t0: 164, t1: 182, light: true }];
const FAR_TRAMS = [{ t0: 28, t1: 52, dir: 1 }, { t0: 70, t1: 94, dir: -1 }, { t0: 132, t1: 156, dir: -1 }, { t0: 214, t1: 238, dir: 1 }];   // line 2 on the Pest quay
const POINTS = [14, 62, 110, 158, 228];             // he points something out to her, 6 s each
const CRUISE = [{ t0: 38, t1: 98, dir: 1 }, { t0: 192, t1: 234, dir: -1 }];
const BARGE = { t0: 104, t1: 176 };
const BELLS = [122, 125, 128];                      // Matthias Church
const SERVE_EVERY = 60, SERVE_AT = 44;              // the chestnut seller hands over a paper cone
const LAMP_FLICK = [[201, 202.4]];                  // the right-hand lamp post sputters once

/* ================= Small pixel helpers ================= */
const RGB = {};
const C = s => RGB[s] || (RGB[s] = hex(s));
/** a pixel buffer to build static layers in: put/has by pixel, flush() writes it to its canvas */
function pix(w = W, h = H) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data;
  const p = { c, x, w, h, d,
    put(i, j, col) {
      if (i < 0 || j < 0 || i >= w || j >= h) return;
      const o = (j * w + i) * 4, k = C(col); d[o] = k[0]; d[o + 1] = k[1]; d[o + 2] = k[2]; d[o + 3] = 255;
    },
    has(i, j) { return i >= 0 && j >= 0 && i < w && j < h && d[(j * w + i) * 4 + 3] > 0; },
    rect(i, j, rw, rh, col) { for (let b = 0; b < rh; b++) for (let a = 0; a < rw; a++) p.put(i + a, j + b, col); },
    flush() { x.putImageData(img, 0, 0); return c; },
  };
  return p;
}
/** a colour of a ramp for v in 0..1, ordered-dithered between neighbours */
function pick(ramp, v, i, j) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > bayer(i, j)) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
const MIXED = {};
function mixHex(a, b, t) {
  const key = a + b + t;
  if (MIXED[key]) return MIXED[key];
  const A = C(a), B = C(b), h2 = v => Math.round(v).toString(16).padStart(2, '0');
  return (MIXED[key] = '#' + h2(lerp(A[0], B[0], t)) + h2(lerp(A[1], B[1], t)) + h2(lerp(A[2], B[2], t)));
}
/** crop a full-screen layer to its painted bounding box so each frame blends only the pixels it needs */
function trim(c) {
  const w = c.width, h = c.height, d = c.getContext('2d').getImageData(0, 0, w, h).data;
  let x0 = w, y0 = h, x1 = -1, y1 = -1;
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) if (d[(y * w + i) * 4 + 3]) {
    if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  if (x1 < 0) return { c, x: 0, y: 0 };
  const [t, tx] = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  tx.drawImage(c, x0, y0, t.width, t.height, 0, 0, t.width, t.height);
  return { c: t, x: x0, y: y0 };
}
const blit = (L, a = 1) => { ctx.globalAlpha = a; ctx.drawImage(L.c, L.x, L.y); ctx.globalAlpha = 1; };
const R = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(x, y, w, h); };
function line(p, x0, y0, x1, y1, col) {              // 1 px line into a pixel buffer, integer endpoints
  const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0)) || 1;
  for (let k = 0; k <= n; k++) p.put(Math.round(x0 + (x1 - x0) * k / n), Math.round(y0 + (y1 - y0) * k / n), col);
}
function flipH(src) {
  const [c, x] = makeCanvas(src.width, src.height);
  x.translate(src.width, 0); x.scale(-1, 1); x.drawImage(src, 0, 0);
  return c;
}
// FONT3 has no U with an umlaut, O with a double acute, or E and A with an acute: the signs here need them
const HU = { u: ['101', '000', '101', '101', '111'], o: ['011', '000', '111', '101', '111'], e: ['001', '111', '110', '100', '111'], a: ['001', '010', '101', '111', '101'] };
/** FONT3 text; lower-case u, o, e, a stand for the accented Hungarian letters above. Returns the width. */
function sign(c, s, x, y, col) {
  let cx = x;
  for (const ch of s) { const g = HU[ch] || FONT3[ch]; if (g) glyph(c, g, cx, y, col); cx += 4; }
  return cx - x - 1;
}

/* ================= Sky: a December night under thin snow cloud, the city's glow along the river ================= */
const SKY = ['#070a1c', '#0a0e24', '#0d122c', '#111734', '#161c3c', '#1c2145', '#23274d', '#2c2c54', '#37325a', '#43395f',
  '#514163', '#604a66', '#705468'].map(hex);
function renderSky() {
  const [c, x] = makeCanvas(W, H);
  const img = x.createImageData(W, H), d = img.data;
  for (let y = 0; y < 180; y++) for (let i = 0; i < W; i++) {
    let v = Math.pow(Math.min(1, y / 165), 1.25) * 0.78;
    v += Math.max(0, 1 - Math.hypot((i - 190) / 200, (y - 150) / 105)) * 0.3;           // the floodlit Parliament
    v += Math.max(0, 1 - Math.hypot((i - 410) / 130, (y - 150) / 70)) * 0.2;            // Castle Hill
    v += Math.max(0, 1 - Math.hypot((i - MOON.x) / 110, (y - MOON.y) / 70)) * 0.2;      // the moon behind the cloud
    const f = clamp(v, 0, 1) * (SKY.length - 1);
    let k = Math.floor(f);
    if (f - k > bayer(i, y)) k++;
    const col = SKY[Math.min(k, SKY.length - 1)], o = (y * W + i) * 4;
    d[o] = col[0]; d[o + 1] = col[1]; d[o + 2] = col[2]; d[o + 3] = 255;
  }
  x.putImageData(img, 0, 0);
  return c;
}
function renderMoon() {
  const r = 6, s = r * 2 + 1; const [c, x] = makeCanvas(s, s);
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - r, dy = y - r, dd = Math.hypot(dx, dy);
    if (dd > r + 0.3) continue;
    let col = dx + dy < -4 ? '#f2f0f6' : '#d6d6e6';
    if (dd > r - 1.2) col = dx + dy > 0 ? '#a2a4c0' : '#c6c8da';
    if (Math.hypot(dx + 2, dy + 1) < 1.4 || Math.hypot(dx - 2, dy - 2) < 1.2) col = '#b6b8ce';
    x.fillStyle = col; x.fillRect(i, y, 1, 1);
  }
  return c;
}
const STARS = [];
{ const r = rng(404); for (let i = 0; i < 46; i++) STARS.push({ x: Math.floor(r() * W), y: Math.floor(r() * r() * 96), k: 20 + Math.floor(r() * 60), p: r(), b: r() }); }

/* ================= Snow clouds (tileable; moonlit tops, bellies lit by the city) ================= */
const CLOUD_DEFS = [
  { seed: 113, y: -10, h: 58, banks: 6, k: 1, pal: { top: '#3b4568', body: '#1c2242', core: '#161b36', bot: '#2c2a4a', bot2: '#23244a' } },
  { seed: 127, y: 22, h: 56, banks: 6, k: 2, pal: { top: '#48507a', body: '#252a4e', core: '#1e2344', bot: '#4a3c5e', bot2: '#342f54' } },
  { seed: 141, y: 66, h: 50, banks: 5, k: 3, pal: { top: '#565a84', body: '#30325a', core: '#292b50', bot: '#6e5470', bot2: '#4a4064' } },
];
function makeCloudLayer(def) {
  const r = rng(def.seed), h = def.h, puffs = [];
  for (let b = 0; b < def.banks; b++) {
    const bx = (b + r() * 0.8) * W / def.banks, bw = 60 + r() * 90, base = h * (0.5 + r() * 0.3);
    const np = 6 + Math.floor(r() * 5);
    for (let i = 0; i < np; i++) {
      const rx = 12 + r() * 20, ry = 4 + r() * 6;
      puffs.push({ x: bx + (r() - 0.5) * bw, y: base - r() * ry * 1.2, rx, ry, base: base + ry * 0.3 });
    }
  }
  const D = new Float32Array(W * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let best = -9;
    for (const p of puffs) {
      let dx = x - p.x; dx -= Math.round(dx / W) * W;
      const dy = y - p.y;
      let v = 1 - (dx * dx) / (p.rx * p.rx) - (dy * dy) / (p.ry * p.ry);
      if (y > p.base) v -= (y - p.base) * 0.35;
      if (v > best) best = v;
    }
    best += 0.16 * Math.sin(TAU * 11 * x / W + y * 0.41 + def.seed) * Math.sin(TAU * 5 * x / W - y * 0.23);
    D[y * W + x] = best;
  }
  const get = (x, y) => (y < 0 || y >= h) ? -9 : D[y * W + ((x % W) + W) % W];
  const [c, cx] = makeCanvas(W, h);
  const img = cx.createImageData(W, h), d = img.data, P = {};
  for (const k in def.pal) P[k] = hex(def.pal[k]);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const th = (bayer(x, y) - 0.5) * 0.3, v = D[y * W + x];
    if (v + th <= 0.03) continue;
    let col;
    if (get(x, y - 1) + th <= 0.03 || get(x, y - 2) <= 0) col = P.top;
    else if (get(x, y + 1) + th <= 0.03) col = P.bot;
    else if (get(x, y + 3) <= 0.05 + bayer(x, y) * 0.2) col = P.bot2;
    else if (v > 0.55 + th) col = P.core;
    else col = P.body;
    const o = (y * W + x) * 4;
    d[o] = col[0]; d[o + 1] = col[1]; d[o + 2] = col[2]; d[o + 3] = 255;
  }
  cx.putImageData(img, 0, 0);
  return { c, y: def.y, k: def.k };
}

/* ================= The Parliament, built in metres and seen in perspective =================
   u runs along the river front from its north corner (0..268 m), v back from the river front towards Kossuth ter
   (0..123 m), z up from its ground. We stand 830 m from the north corner, on the river side, looking along the front
   at about 35 degrees, so the north end shows left of it and everything set back (the dome, the two chambers'
   roofs, the towers of the east front) slides left behind the nearer roofs: a forest of spires at every depth. */
const PF = 780, PVX = 1.15, PXC = 231, CAMZ = 7;     // focal length (px), vertical stretch, the view axis' column, eye above its ground (m)
const pf = (u, v) => 831.9 + 0.8194 * u + 0.5735 * v;     // metres ahead of us
const pr = (u, v) => -78.7 + 0.5739 * u - 0.8194 * v;     // metres right of the view axis
const px3 = (u, v) => PXC + PF * pr(u, v) / pf(u, v);
const py3 = (u, v, z) => YH - PVX * PF * (z - CAMZ) / pf(u, v);
const GOLD = ['#2e1c14', '#4e3020', '#74492a', '#a06c34', '#c89444', '#e6b85e', '#f8d686', '#ffeeb4', '#fff9dc'];   // floodlit limestone
const SLATE = ['#191b2c', '#22253a', '#2c2f46', '#3a3e58', '#515874'];
const DOME = ['#341416', '#52201e', '#742c24', '#9a402c', '#c05c38', '#e2844e'];
const GLASS = '#33242c', LIT_A = '#fff0b4', LIT_B = '#ffc868';
const FLAG_AT = { x: Math.round(px3(0, 61.5)), y: Math.round(py3(0, 61.5, 46)) };   // the flagstaff on the north gable
function drawParliament(p) {                         // into the land layer's pixel buffer; returns its switching windows
  const r = rng(1896), wins = [], glass = [];
  /** a wall in the plane v = const, from u0 to u1 and z0 to z1; shade(metres along, z, x, y) gives each pixel's colour */
  const faceV = (v, u0, u1, z0, z1, shade) => {
    for (let x = Math.round(px3(u0, v)), xb = Math.round(px3(u1, v)); x < xb; x++) {
      const k = (x + 0.5 - PXC) / PF, u = (k * (831.9 + 0.5735 * v) + 78.7 + 0.8194 * v) / (0.5739 - 0.8194 * k), q = PVX * PF / pf(u, v);
      for (let y = Math.round(YH - (z1 - CAMZ) * q), yb = Math.round(YH - (z0 - CAMZ) * q); y < yb; y++) {
        const col = shade(u, CAMZ + (YH - y - 0.5) / q, x, y); if (col) p.put(x, y, col);
      }
    }
  };
  /** a wall in the plane u = const, from v0 to v1 (v1 further from the river, so further left) */
  const faceU = (u, v0, v1, z0, z1, shade) => {
    for (let x = Math.round(px3(u, v1)), xb = Math.round(px3(u, v0)); x < xb; x++) {
      const k = (x + 0.5 - PXC) / PF, v = (-78.7 + 0.5739 * u - k * (831.9 + 0.8194 * u)) / (0.8194 + 0.5735 * k), q = PVX * PF / pf(u, v);
      for (let y = Math.round(YH - (z1 - CAMZ) * q), yb = Math.round(YH - (z0 - CAMZ) * q); y < yb; y++) {
        const col = shade(v, CAMZ + (YH - y - 0.5) / q, x, y); if (col) p.put(x, y, col);
      }
    }
  };
  const poly = (pts, shade) => {                      // convex polygon of screen points
    let y0 = Infinity, y1 = -Infinity;
    for (const [, y] of pts) { y0 = Math.min(y0, y); y1 = Math.max(y1, y); }
    for (let y = Math.round(y0); y < Math.round(y1); y++) {
      let a = Infinity, b = -Infinity; const yc = y + 0.5;
      for (let i = 0; i < pts.length; i++) {
        const [xa, ya] = pts[i], [xb, yb] = pts[(i + 1) % pts.length];
        if ((ya <= yc) === (yb <= yc)) continue;
        const x = xa + (xb - xa) * (yc - ya) / (yb - ya);
        a = Math.min(a, x); b = Math.max(b, x);
      }
      for (let x = Math.round(a), xe = Math.max(Math.round(a) + 1, Math.round(b)); x < xe; x++) { const col = shade(x, y); if (col) p.put(x, y, col); }
    }
  };
  const P3 = (u, v, z) => [px3(u, v), py3(u, v, z)];
  // stone, floodlit from the ground: bays of 8 m with a buttress pier each, string courses, a pierced parapet on top
  const wall = (dim, top = 22) => (a, z, x, y) => {
    const m = ((a % 8) + 8) % 8;
    let v = 0.84 - z * 0.012 - dim;
    if (m < 1.4) v += 0.1; else if (m > 6.8) v -= 0.13;
    if (z >= top) { if (m >= 1.4 && ((x + y) & 1)) v -= 0.42; else v += 0.06; }
    else if (z > top - 1.6) v += 0.12;
    else if (z > 9.6 && z < 10.9) v += 0.1;
    else if (z < 1.5) v -= 0.22;
    return pick(GOLD, v, x, y);
  };
  // slate: steep roofs, a dusting of snow on the ridges, a little warmth from the floodlights at the eaves
  const slate = (dim = 0) => (a, z, x, y) => {
    const h1 = hash(x, y, 71);
    if (h1 < 0.07) return '#77829f';
    return pick(SLATE, 0.5 - dim + (h1 - 0.5) * 0.3 + ((x + 2 * y) % 5 === 0 ? 0.12 : 0), x, y);
  };
  const flat = (ramp, v) => (x, y) => pick(ramp, v + (hash(x, y, 72) - 0.5) * 0.12, x, y);
  /** a square turret centred on (u, v): shaft from z0 to z1, half m wide each way, then a spire up to z2 */
  const turret = (u, v, half, z0, z1, z2, dim = 0) => {
    const cx = px3(u, v), q = PVX * PF / pf(u, v), w = Math.max(1, Math.round(half * 2 * PF / pf(u, v))), x0 = Math.round(cx - w / 2);
    const ya = Math.round(YH - (z1 - CAMZ) * q), yb = Math.round(YH - (z0 - CAMZ) * q), yt = Math.round(YH - (z2 - CAMZ) * q);
    for (let y = ya; y < yb; y++) for (let i = 0; i < w; i++) {
      let val = 0.78 - dim - (yb - y) / (yb - yt + 1) * 0.2 + (w > 1 ? (i >= w * 0.45 ? 0.08 : -0.14) : 0);
      if (y === ya) val += 0.14;
      else if (w >= 3 && i > 0 && i < w - 1 && (i & 1) === (w >= 5 ? 0 : 1) && y > ya + 1 && y < ya + 2 + Math.max(2, (yb - ya) * 0.34)) val = 0.1;   // belfry lights
      p.put(x0 + i, y, pick(GOLD, val, x0 + i, y));
    }
    if (w >= 3) for (const i of [0, w - 1]) { p.put(x0 + i, ya - 1, GOLD[6]); if (w >= 5) p.put(x0 + i, ya - 2, GOLD[7]); }   // corner pinnacles
    for (let y = yt; y < ya; y++) {                    // the spire
      const s = (y - yt) / (ya - yt), hw = s * w / 2, xl = Math.round(cx - hw), xr = Math.max(xl, Math.round(cx + hw) - 1);
      for (let x = xl; x <= xr; x++) p.put(x, y, pick(GOLD, 0.66 - dim - (1 - s) * 0.16 + (x > cx - 0.5 ? 0.1 : -0.12), x, y));
      if (w >= 4 && (y - yt) % 3 === 2 && s > 0.2) { p.put(xl - 1, y, GOLD[4]); p.put(xr + 1, y, GOLD[5]); }   // crockets
    }
    p.put(Math.round(cx - 0.5), yt - 1, GOLD[7]);
  };
  /** an opening on the river-facing plane v at u (wm metres wide, z0..z1): remembered, painted after the stone */
  const openV = (u, v, z0, z1, wm, kind) => {
    const x0 = Math.round(px3(u - wm / 2, v)), w = Math.max(1, Math.round(px3(u + wm / 2, v) - px3(u - wm / 2, v)));
    const y0 = Math.round(py3(u, v, z1)); glass.push({ x: x0, y: y0, w, h: Math.max(2, Math.round(py3(u, v, z0)) - y0), kind });
  };
  const openU = (u, v, z0, z1, wm, kind) => {
    const x0 = Math.round(px3(u, v + wm / 2)), w = Math.max(1, Math.round(px3(u, v - wm / 2) - px3(u, v + wm / 2)));
    const y0 = Math.round(py3(u, v, z1)); glass.push({ x: x0, y: y0, w, h: Math.max(2, Math.round(py3(u, v, z0)) - y0), kind });
  };
  /** a steep hipped roof over the block u0..u1, v0..v1: eaves at z0, a ridge along u at z1, turrets on its corners */
  const chamber = (u0, u1, v0, v1, z0, z1, dim) => {
    const vm = (v0 + v1) / 2, ua = u0 + 10, ub = u1 - 10;
    poly([P3(u0, v1, z0), P3(u0, v0, z0), P3(ua, vm, z1)], flat(SLATE, 0.3 - dim));                       // north hip
    poly([P3(u0, v0, z0), P3(u1, v0, z0), P3(ub, vm, z1), P3(ua, vm, z1)], flat(SLATE, 0.62 - dim));     // river slope
    for (let x = Math.round(px3(ua, vm)); x <= Math.round(px3(ub, vm)); x++) p.put(x, Math.round(py3(ua, vm, z1)) - 1, '#8e98b6');   // snow on the ridge
    for (const [u, v] of [[u0, v1], [u1, v1], [u1, v0], [u0, v0]]) turret(u, v, 2.6, z0 - 6, z0 + 16, z0 + 28, dim);
    turret((ua + ub) / 2, vm, 1.4, z1 - 1, z1 + 3, z1 + 12, dim);                                          // a fleche on the ridge
  };

  // ---- the east front's two tall towers, far side of the building
  turret(156, 116, 4.2, 20, 52, 73, 0.14); turret(112, 116, 4.2, 20, 52, 73, 0.12);
  // ---- the south chamber's roof and turrets
  chamber(174, 214, 40, 84, 30, 44, 0.08);
  // ---- the dome: sixteen-sided drum with tall windows, buttress pinnacles, ribbed red shell, lantern and spire
  {
    const u = 134, v = 61.5, cx = px3(u, v), sx = PF / pf(u, v), q = PVX * PF / pf(u, v), Rd = 13.2 * sx, Y = z => YH - (z - CAMZ) * q, ci = Math.round(cx);
    for (let y = Math.round(Y(53)); y < Math.round(Y(28)); y++) for (let x = Math.round(cx - Rd); x <= Math.round(cx + Rd); x++) {
      const a = (x - cx) / Rd, seg = ((x - ci) % 3 + 3) % 3;
      let val = 0.66 + 0.2 * a - 0.3 * a * a;
      if (seg === 0) val += 0.16; else if (seg === 1 && y > Y(50) && y < Y(37)) val = 0.14;
      if (y === Math.round(Y(53))) val += 0.2;
      p.put(x, y, pick(GOLD, val, x, y));
    }
    for (let x = Math.round(cx - Rd); x <= Math.round(cx + Rd); x++) if (((x - ci) % 3 + 3) % 3 === 0) {   // pinnacles round the drum
      const y0 = Math.round(Y(53)); p.put(x, y0 - 1, GOLD[6]); p.put(x, y0 - 2, GOLD[6]); p.put(x, y0 - 3, GOLD[7]);
      if (Math.abs(x - cx) > Rd - 2) p.put(x, y0 - 4, GOLD[7]);
    }
    for (let y = Math.round(Y(74)); y < Math.round(Y(53)); y++) {
      const h = (Y(53) - y - 0.5) / (Y(53) - Y(74)), w = Rd * 0.94 * Math.pow(Math.cos(h * Math.PI / 2), 0.7);
      for (let x = Math.round(cx - w); x <= Math.round(cx + w) - 1; x++) {
        const a = (x + 0.5 - cx) / Math.max(1, w);
        let val = 0.46 + 0.26 * a - 0.32 * a * a - h * 0.1;
        for (const ra of [-0.86, -0.46, 0, 0.46, 0.86]) if (Math.abs(x + 0.5 - (cx + ra * w)) < 0.5) val += 0.26;   // ribs
        p.put(x, y, pick(DOME, val, x, y));
      }
    }
    for (let x = ci - 3; x <= ci + 2; x++) { p.put(x, Math.round(Y(74)), GOLD[7]); p.put(x, Math.round(Y(74)) - 1, (x & 1) ? GOLD[6] : GOLD[3]); }   // the gallery
    for (let y = Math.round(Y(84)); y < Math.round(Y(74)) - 1; y++) for (let x = ci - 2; x <= ci + 1; x++) p.put(x, y, x === ci - 1 && (y & 1) ? GOLD[2] : x < ci ? GOLD[5] : GOLD[7]);
    for (let y = Math.round(Y(96)); y < Math.round(Y(84)); y++) { p.put(ci - 1, y, y > Y(90) ? GOLD[5] : GOLD[6]); if (y > Y(89)) p.put(ci, y, GOLD[7]); }
    p.put(ci - 1, Math.round(Y(96)) - 1, GOLD[8]);
  }
  // ---- the north chamber, nearer than the dome
  chamber(54, 94, 40, 84, 30, 44, 0.02);
  // ---- the north end: pavilions at both corners, a gabled centre, bays of tall windows
  faceU(-2, 109, 127, 0, 27, wall(0.2, 25));
  poly([P3(-2, 127, 27), P3(-2, 109, 27), P3(7, 118, 41)], flat(SLATE, 0.34));
  turret(-2, 127, 1.9, 18, 33, 46, 0.14); turret(-2, 109, 1.9, 18, 33, 46, 0.1);
  faceU(4, 14, 109, 24, 33, slate(0.1));
  faceU(0, 14, 109, 0, 24, wall(0.16));
  poly([P3(0, 72, 24), P3(0, 51, 24), P3(0, 61.5, 39)], flat(GOLD, 0.6));
  turret(0, 72, 1.5, 20, 34, 44, 0.1); turret(0, 51, 1.5, 20, 34, 44, 0.06);
  for (let v = 14; v + 8 <= 109; v += 8) if (v + 4 < 50 || v + 4 > 73) { openU(0, v + 4.4, 11.5, 19.4, 3, 'win'); openU(0, v + 4.4, 2.6, 8.6, 3.6, 'door'); }
  openU(0, 61.5, 12, 30, 7, 'big'); openU(0, 61.5, 1.5, 9, 6, 'door');
  openU(-2, 118, 11, 21, 5, 'win');
  for (let v = 14; v < 109; v += 8) { const x = Math.round(px3(0, v)), y = Math.round(py3(0, v, 24)); p.put(x, y - 1, GOLD[5]); p.put(x, y - 2, GOLD[6]); p.put(x, y - 3, GOLD[7]); }
  for (let y = FLAG_AT.y; y < Math.round(py3(0, 61.5, 39)); y++) p.put(FLAG_AT.x, y, '#b8a888');             // flagstaff (the flag is drawn per frame)
  // ---- the river wing: roof, fleches, then the long front with its arcade and tall windows
  faceV(6, 14, 254, 24, 33, slate(0));
  for (const u of [34, 64, 92, 176, 206, 236]) turret(u, 6, 1.3, 31, 35, 43);
  faceV(0, 14, 254, 0, 24, wall(0));
  for (let u = 16; u + 8 <= 252; u += 8) {
    if (u + 4 > 106 && u + 4 < 162) continue;
    openV(u + 4.4, 0, 11.5, 19.4, 3, 'win'); openV(u + 4.4, 0, 2.6, 8.8, 4.4, 'arch');
  }
  for (let u = 16; u <= 252; u += 8) {                 // a pinnacle on every pier, a taller turret now and then
    if (u > 106 && u < 162) continue;
    const x = Math.round(px3(u, 0)), y = Math.round(py3(u, 0, 24));
    p.put(x, y - 1, GOLD[6]); p.put(x, y - 2, GOLD[7]); p.put(x, y - 3, GOLD[7]); if (u < 130) p.put(x, y - 4, GOLD[8]);
  }
  for (const u of [48, 80, 184, 216]) turret(u, 0, 1.7, 22, 31, 40);
  // ---- the centre of the river front: a taller block set forward, a tower on each side of it
  faceV(-3, 114, 154, 28, 36, slate(0));
  faceU(108, -7, 0, 0, 28, wall(0.18, 26));
  faceV(-7, 108, 160, 0, 28, wall(-0.04, 26));
  for (let u = 118; u < 150; u += 6.6) { openV(u + 3.3, -7, 12, 23, 2.8, 'win'); openV(u + 3.3, -7, 2.6, 9, 4, 'arch'); }
  turret(156, -7, 4, 24, 50, 73); turret(112, -7, 4, 24, 50, 73);
  // ---- the corner pavilions: south-west, then the nearest one, north-west
  faceV(-4, 252, 270, 0, 27, wall(0, 25));
  poly([P3(252, -4, 27), P3(270, -4, 27), P3(261, 5, 41)], flat(SLATE, 0.6));
  turret(270, -4, 1.9, 18, 33, 46); turret(252, -4, 1.9, 18, 33, 46);
  openV(261, -4, 11, 21, 5, 'win'); openV(261, -4, 2.6, 8.8, 5, 'arch');
  faceU(-2, -4, 14, 0, 27, wall(0.16, 25));
  faceV(-4, -2, 16, 0, 27, wall(-0.03, 25));
  poly([P3(-2, 14, 27), P3(-2, -4, 27), P3(7, 5, 41)], flat(SLATE, 0.32));
  poly([P3(-2, -4, 27), P3(16, -4, 27), P3(7, 5, 41)], flat(SLATE, 0.64));
  turret(-2, 14, 1.9, 18, 33, 46, 0.1); turret(16, -4, 1.9, 18, 33, 46); turret(-2, -4, 1.9, 18, 33, 47);
  openV(7, -4, 11, 21, 5, 'win'); openV(7, -4, 2.6, 8.8, 5, 'arch'); openU(-2, 5, 11, 21, 5, 'win');

  // ---- glass: dark at night, a few rooms lit, some switching (those go to `wins`); the arcade glows from inside
  for (const g of glass) {
    if (g.kind === 'arch' || g.kind === 'door') {
      for (let j = 0; j < g.h; j++) for (let i = 0; i < g.w; i++) {
        if (j === 0 && g.w >= 3 && (i === 0 || i === g.w - 1)) continue;                 // a pointed head
        p.put(g.x + i, g.y + j, g.kind === 'door' ? (j < g.h * 0.5 ? '#2c1a16' : '#5a3418') : j < g.h * 0.34 ? '#3a2014' : j < g.h * 0.7 ? '#8a5020' : '#c8802e');
      }
      continue;
    }
    const v = r(), lit = v < 0.2;
    for (let j = 0; j < g.h; j++) for (let i = 0; i < g.w; i++) {
      if (j === 0 && g.w >= 3 && (i === 0 || i === g.w - 1)) continue;
      p.put(g.x + i, g.y + j, lit ? (j < g.h * 0.5 ? LIT_A : LIT_B) : (g.kind === 'big' && (i + j) % 3 === 0 ? '#5a3c34' : GLASS));
    }
    if (!lit && v < 0.48) wins.push({ id: wins.length, x: g.x, y: g.y + (g.w >= 3 ? 1 : 0), w: g.w, h: g.h - (g.w >= 3 ? 1 : 0), n: 3 + Math.floor(r() * 6), p: 0.4 + r() * 0.25, off: r() });
  }
  return wins;
}

/* ================= The far shore: Gellert Hill, the Chain Bridge, Castle Hill and the Buda bank ================= */
const CHAIN = { x0: 314, x1: 382, t: [331, 365], deck: 152, top: 137 };
const LIBERTY = { x: 343, y: 126 };
const PALACE = { x0: 377, x1: 407, dome: 392 };
const MATTHIAS = { x: 427, tip: 104 };
const HILL_PTS = [[360, 157], [368, 149], [377, 142], [408, 141], [418, 139], [446, 138], [464, 136], [481, 134]];
const hillY = x => { let k = 0; while (HILL_PTS[k + 1][0] < x) k++; const [x0, y0] = HILL_PTS[k], [x1, y1] = HILL_PTS[k + 1]; return Math.round(lerp(y0, y1, (x - x0) / (x1 - x0))); };
const gellertY = x => { const a = (x - 338) / 30; return 133 + Math.round(a * a * 21 + (x > 338 ? (x - 338) * 0.1 : 0)); };
const BASTION = [[412, 3], [417, 2], [422, 3], [432, 3], [437, 2], [442, 3]];   // the Fisherman's Bastion's turrets: column, size
const STONE = ['#4a4660', '#6e6876', '#968c86', '#beb096', '#e0d2a8', '#f6ecc6'];   // pale floodlit stone, far off
function chainCurve(x) {                               // row of the Chain Bridge's upper chain at column x
  const [a, b] = CHAIN.t, sag = (x0, x1, y0, y1, d) => { const s = (x - x0) / (x1 - x0); return lerp(y0, y1, s) + d * 4 * s * (1 - s); };
  if (x < a - 3) return sag(CHAIN.x0, a - 3, CHAIN.deck - 1, CHAIN.top + 2, 2.5);
  if (x <= a + 3) return null;
  if (x < b - 3) return sag(a + 3, b - 3, CHAIN.top + 2, CHAIN.top + 2, 9.5);
  if (x <= b + 3) return null;
  return sag(b + 3, CHAIN.x1, CHAIN.top + 2, CHAIN.deck - 1, 2.5);
}
function drawFar(p) {
  const r = rng(1873), wins = [];
  const addWin = (x, y, col, pr = 0.5) => wins.push({ id: 500 + wins.length, x, y, col, n: 2 + Math.floor(r() * 6), p: pr, off: r() });
  // Gellert Hill, dark and wooded, snow on its crown; the Citadella's wall and the Liberty Statue lit on top
  for (let x = 302; x <= 374; x++) for (let y = gellertY(x); y < 157; y++) {
    const d = y - gellertY(x), h1 = hash(x, y, 31);
    p.put(x, y, d < 2 && h1 < 0.6 ? '#48507a' : h1 < 0.12 ? '#39416a' : h1 > 0.985 ? '#c8a66a' : d < 6 ? '#282e52' : '#20254a');
  }
  for (let x = 328; x <= 340; x++) { p.put(x, gellertY(x) - 1, '#8a8490'); p.put(x, gellertY(x), '#5c5870'); }
  { const { x, y } = LIBERTY; p.rect(x - 1, y + 6, 3, 3, '#aaa290'); p.rect(x, y + 2, 1, 4, '#f2ecd2'); p.put(x, y + 1, '#fff8e0');
    p.put(x - 1, y, '#e8e0c0'); p.put(x, y, '#fff8e0'); p.put(x + 1, y, '#e8e0c0'); p.put(x - 1, y + 3, '#d8d0b4'); }
  // the far Pest embankment between the Parliament and the Chain Bridge, and the city beyond the bridge
  for (let x = 284; x < 318; x++) {
    const top = 150 - Math.floor(hash(x >> 2, 3, 33) * 4) - (x < 300 ? 1 : 0);
    for (let y = top; y < 158; y++) p.put(x, y, y === top ? '#5a5874' : (x & 3) === 0 ? '#2e2c46' : '#3a3852');
    if (hash(x, 5, 33) < 0.45) p.put(x, top + 2 + Math.floor(hash(x, 6, 33) * 3), hash(x, 7, 33) < 0.7 ? '#e8b868' : '#ffe2a0');
  }
  for (let x = 316; x <= 384; x++) for (let y = 151; y < 156; y++) p.put(x, y, y === 151 ? '#3c3a58' : hash(x, y, 34) < 0.3 ? '#d8aa68' : '#262844');
  for (const ex of [338, 357]) { p.put(ex, 148, '#b8c0d8'); p.put(ex, 149, '#98a0be'); p.put(ex, 150, '#98a0be'); }    // the Elisabeth Bridge's white pylons
  // Castle Hill: wooded slope, lit ramparts, snow along the top
  for (let x = 360; x < W; x++) for (let y = hillY(x); y < BANKY[x]; y++) {
    const d = y - hillY(x), h1 = hash(x, y, 35);
    let col = h1 < 0.14 ? '#3a4470' : d < 5 ? '#272d54' : '#1f2448';
    if (d === 0) col = h1 < 0.75 ? '#7c88ac' : '#4a527c';
    if (x > 370 && x < 448 && (d === 6 || d === 7) && h1 < 0.86) col = d === 6 ? '#c6b48a' : '#8e7e68';      // the castle wall
    if (x > 380 && x < 440 && d === 12 + ((x >> 3) & 1) && h1 < 0.7) col = '#8a7c6c';
    p.put(x, y, col);
  }
  // the Royal Palace: a long pale front on the hill's south end, its green dome in the middle
  for (let x = PALACE.x0; x <= PALACE.x1; x++) {
    const wing = x < PALACE.x0 + 6 || x > PALACE.x1 - 6, mid = Math.abs(x - PALACE.dome) <= 5, top = mid ? 131 : wing ? 133 : 134;
    for (let y = top; y <= hillY(x) + 1; y++) {
      let col = pick(STONE, 0.5 + (y - top) * 0.07 + (mid ? 0.1 : 0), x, y);
      if (y === top) col = mid ? '#fff6d6' : '#7c8a8a';
      else if (y > top + 1 && y < top + 7 && (x & 1) && (y & 1)) col = '#6a5a56';
      p.put(x, y, col);
    }
  }
  for (let j = 0; j < 6; j++) { const hw = [1, 2, 3, 3, 4, 4][j]; for (let i = -hw; i <= hw; i++) p.put(PALACE.dome + i, 125 + j, i < -1 ? '#4e7e72' : i > hw - 2 ? '#a6d0b8' : '#78ae9a'); }
  p.put(PALACE.dome, 124, '#e8e0c0'); p.put(PALACE.dome, 123, '#fff6d6'); p.put(PALACE.dome, 122, '#c8c0a0');
  // houses of the Castle District along the ridge
  for (let x = 408; x < 446; x++) {
    const top = hillY(x) - 3 - Math.floor(hash(x >> 2, 9, 36) * 3);
    for (let y = top; y < hillY(x); y++) p.put(x, y, y === top ? '#8a94b4' : (x & 3) === 1 && y === top + 2 ? '#e6b060' : '#4e4a62');
  }
  // Matthias Church: nave under its patterned tile roof, the tall lacy south tower, the low Bela tower
  { const X = MATTHIAS.x, T = ['#c8683a', '#e6b44e', '#4f7f5e'];
    for (let y = 126; y <= 138; y++) for (let x = X - 8; x <= X + 9; x++) {
      const roofTop = 126 + Math.abs(x - (X + 1)) * 0.3;
      if (y < roofTop) continue;
      p.put(x, y, y < 132 ? T[(x + y * 2) % 3] : y === 132 ? '#fff4cc' : (x % 3 === 0 && y < 137 ? '#5a4a50' : pick(STONE, 0.72, x, y)));
    }
    for (let y = 119; y <= 138; y++) for (let x = X - 2; x <= X + 1; x++)                      // the tower's shaft
      p.put(x, y, y === 119 || y === 126 ? '#fff6d6' : (x === X - 1 || x === X) && (y % 4 === 1) ? '#4a3e48' : pick(STONE, x < X ? 0.66 : 0.9, x, y));
    for (let y = MATTHIAS.tip; y < 119; y++) {                                                  // and its stone spire
      const s = (y - MATTHIAS.tip) / (119 - MATTHIAS.tip), hw = s * 2;
      for (let x = Math.round(X - 0.5 - hw); x <= Math.round(X - 0.5 + hw) - (hw < 0.5 ? 0 : 0); x++) p.put(x, y, pick(STONE, x < X ? 0.7 : 0.95, x, y));
      if ((y - MATTHIAS.tip) % 4 === 3 && s > 0.3) { p.put(Math.round(X - 1.5 - hw), y, '#beb096'); p.put(Math.round(X + 0.5 + hw), y, '#e0d2a8'); }
    }
    p.put(X - 3, 118, '#f6ecc6'); p.put(X + 2, 118, '#f6ecc6'); p.put(X - 3, 117, '#e0d2a8'); p.put(X + 2, 117, '#e0d2a8');
    for (let y = 123; y <= 131; y++) for (let x = X - 8; x <= X - 6; x++) p.put(x, y, y < 126 ? T[(x + y) % 3] : pick(STONE, 0.7, x, y));
    p.put(X - 7, 122, T[1]); p.put(X - 7, 121, '#fff6d6');
  }
  // the Fisherman's Bastion in front of it: a pale arcaded wall and its cone-capped turrets
  for (let x = 409; x <= 445; x++) for (let y = 137; y <= 141; y++) p.put(x, y, y === 137 ? '#fff6d6' : y < 141 && x % 3 === 1 ? '#5c5262' : pick(STONE, 0.86 - (y - 137) * 0.05, x, y));
  for (const [bx, s] of BASTION) {
    for (let j = 0; j < s + 1; j++) for (let i = -1; i <= 1; i++) p.put(bx + i, 136 - j, i === 0 && j === 1 ? '#5c5262' : pick(STONE, i < 0 ? 0.7 : 0.95, bx + i, 136 - j));
    for (let j = 0; j < s; j++) { const hw = j < s - 1 ? 1 : 0; for (let i = -hw; i <= hw; i++) p.put(bx + i, 135 - s - j, i < 0 ? '#c8bc9c' : '#fff8dc'); }
  }
  // Vizivaros on the Buda bank: a row of houses under the hill, the Calvinist church, St Anne's two towers
  for (let x = 384; x < 446; x++) {
    const hs = Math.floor((x - 384) / 7), top = BANKY[x] - 10 - Math.floor(hash(hs, 2, 37) * 4) - Math.floor((x - 384) / 12);
    const col = ['#4a4460', '#5c4c58', '#3e4060', '#54506a'][Math.floor(hash(hs, 3, 37) * 4)];
    for (let y = top; y < BANKY[x] - 3; y++) {
      p.put(x, y, y === top ? '#98a2c2' : y === top + 1 ? '#34324a' : (x - 384) % 7 === 0 ? '#2a2a42' : col);
      if (y > top + 2 && (y - top) % 3 === 0 && (x - 384) % 7 % 2 === 1) { const h1 = r(); if (h1 < 0.3) p.put(x, y, '#f0b866'); else if (h1 < 0.55) addWin(x, y, '#f0b866'); }
    }
  }
  { const X = 410, T = ['#b85a34', '#d8a848', '#3e6e54'];                                       // the Calvinist church (Szilagyi Dezso ter)
    for (let y = 149; y < BANKY[X] - 3; y++) for (let x = X - 4; x <= X + 4; x++) p.put(x, y, y < 152 ? T[(x + y) % 3] : x % 3 === 0 ? '#3a2e36' : '#8a5a48');
    for (let y = 139; y < 149; y++) { const hw = y < 145 ? (y - 139) / 6 * 1.5 : 1.5; for (let x = Math.round(X - hw); x <= Math.round(X + hw); x++) p.put(x, y, y < 145 ? T[(x + y) % 3] : y === 145 ? '#e8d8b0' : '#8a5a48'); }
    p.put(X, 138, '#e8d8b0');
  }
  for (const X of [419, 425]) {                                                                // St Anne's (Batthyany ter)
    for (let y = 142; y < BANKY[X] - 3; y++) for (let x = X - 1; x <= X + 1; x++) p.put(x, y, y === 147 ? '#fff0c0' : x === X && (y === 144 || y === 150) ? '#4a3e48' : x < X ? '#b89c64' : '#e2c880');
    p.put(X, 141, '#6aa690'); p.put(X - 1, 141, '#4e8672'); p.put(X + 1, 141, '#8ac4a8'); p.put(X, 140, '#6aa690'); p.put(X, 139, '#8ac4a8'); p.put(X, 138, '#e8e0c0');
  }
  for (let x = 421; x <= 423; x++) for (let y = 147; y < BANKY[x] - 3; y++) p.put(x, y, y === 147 ? '#fff0c0' : '#c8ae70');
  // Bem rakpart at the right edge: two big blocks, nearer and taller, snow on their roofs
  for (const [x0, x1, t0, t1, col] of [[446, 461, 134, 129, '#3e3a56'], [462, 480, 124, 115, '#463c52']]) for (let x = x0; x <= x1; x++) {
    const top = Math.round(lerp(t0, t1, (x - x0) / (x1 - x0))), i = x - x0;
    for (let y = top; y < BANKY[x] - 3; y++) {
      const d = y - top;
      let c2 = d === 0 ? '#a4aed0' : d < 3 ? '#2a2a44' : d === 3 ? '#6a6682' : i === 0 ? '#2a2840' : col;
      if (d > 4 && d % 5 >= 1 && d % 5 <= 2 && i % 4 >= 1 && i % 4 <= 2) {
        const h1 = hash(i >> 2, Math.floor(d / 5), 38 + x0);
        c2 = h1 < 0.26 ? '#ffca78' : h1 < 0.3 ? '#9ab6e6' : '#1e1e36';
        if (h1 >= 0.3 && h1 < 0.5 && d % 5 === 1 && i % 4 === 1) addWin(x, y, '#ffca78', 0.5);
      }
      if (y > BANKY[x] - 12 && d > 4) c2 = mixHex(c2, '#c08a50', 0.3);                          // street light on the ground floor
      p.put(x, y, c2);
    }
  }
  // the Chain Bridge: two stone gates, the deck, the chains picked out in bulbs
  for (let x = CHAIN.x0; x <= CHAIN.x1; x++) { p.put(x, CHAIN.deck, '#e2d09a'); p.put(x, CHAIN.deck + 1, '#6a607a'); if (x % 3 === 0) p.put(x, CHAIN.deck + 2, '#4a4662'); }
  for (let x = CHAIN.x0; x <= CHAIN.x1; x++) {
    const cy = chainCurve(x); if (cy === null) continue;
    const y = Math.round(cy);
    if (x % 2 === 0) for (let k = y + 2; k < CHAIN.deck; k += 1) if ((k & 1) === 0) p.put(x, k, '#5a5876');   // suspenders
    p.put(x, y + 1, '#a08860');
  }
  for (const tx of CHAIN.t) {
    for (let y = CHAIN.top; y <= 157; y++) {
      const hw = y <= CHAIN.top + 1 ? 4 : y > 153 ? 4 : 3;
      for (let x = tx - hw; x <= tx + hw; x++) {
        let col = pick(STONE, 0.6 + (y - CHAIN.top) * 0.012 + (x < tx ? -0.1 : 0.12), x, y);
        if (y === CHAIN.top) col = '#fff6d6';
        else if (Math.abs(x - tx) <= 1 && y >= 144 && y < CHAIN.deck && !(y === 144 && x !== tx)) col = '#2a2640';   // the gateway
        else if (y === CHAIN.top + 5) col = '#8a7e78';
        p.put(x, y, col);
      }
    }
  }
  return wins;
}

/* ================= The Pest bank on the left: a riverside block (the roof sign stands on it), the quay ================= */
const BLOCK = { x1: 74, top: x => Math.round(lerp(104, 112, x / 74)), base: x => Math.round(lerp(163, 160, x / 74)) };
const CHIM = { x: 12, y: 93 };                        // its chimney, smoking
function drawPest(p) {
  const r = rng(1900), wins = [];
  // the tip of St Stephen's Basilica over the roofs (most of it is hidden behind the riverside blocks from here)
  for (let j = 0; j < 5; j++) { const hw = [0, 1, 2, 3, 3][j]; for (let i = -hw; i <= hw; i++) p.put(24 + i, 98 + j, i < 0 ? '#4c5676' : '#6c7898'); }
  p.put(24, 97, '#9aa4c0'); p.put(24, 96, '#c8cce0');
  for (let x = -8; x <= BLOCK.x1; x++) {
    const top = BLOCK.top(x), base = BLOCK.base(x), fh = (base - top - 8) / 6, bay = Math.floor((x + 8) / 7), m = (x + 8) % 7;
    for (let y = top - 5; y < base; y++) {
      let col;
      if (y < top) col = y === top - 5 ? '#a6b0d2' : (m === 3 && y > top - 4 && bay % 2 === 0) ? '#e8b060' : hash(x, y, 41) < 0.1 ? '#7c88ac' : '#262840';   // mansard, dormers, snow
      else if (y === top) col = '#7a7896';
      else if (y === top + 1) col = '#2e2c46';
      else {
        const fl = (y - top - 2) / fh, fy = fl - Math.floor(fl), ground = y >= base - 8;
        col = m === 0 ? '#2f2d48' : ground ? '#4a3c44' : '#3c3a56';
        if (!ground && m >= 2 && m <= 4 && fy > 0.2 && fy < 0.75) {
          const id = bay * 16 + Math.floor(fl), h1 = hash(id, 1, 42);
          col = h1 < 0.24 ? '#ffc874' : h1 < 0.28 ? '#8eaede' : '#1b1c34';
          if (h1 >= 0.28 && h1 < 0.5 && m === 2 && Math.abs(fy - 0.3) < 0.5 / fh) wins.push({ id: 700 + id, x, y, w: 3, h: Math.max(2, Math.round(fh * 0.55)), col: '#ffc874', n: 2 + Math.floor(r() * 5), p: 0.5, off: r() });
        }
        if (ground && y > base - 7 && y < base - 1 && m !== 0) col = bay % 3 === 1 ? '#3a2c34' : pick(['#c07c36', '#f0b45c', '#ffd88a'], 0.4 + (base - y) * 0.09, x, y);   // lit shop fronts
        if (y === base - 8) col = '#6c6480';
      }
      p.put(x, y, col);
    }
  }
  for (let j = 0; j < 7; j++) for (let i = 0; i < 3; i++) p.put(CHIM.x - 1 + i, CHIM.y + j, j === 0 ? '#a6b0d2' : i === 0 ? '#5a4a52' : '#3a3044');
  return wins;
}
const SHOP_SIGN = { x: 18, y: 0, s: 'KaVeHaZ' };       // KAVEHAZ with its accents (see `sign`); the row is set in renderLand

/* ================= The land as one layer; the quays under it; its mirror image for the river ================= */
const PEST_LAMPS = [10, 38, 66, 100, 132, 190, 214, 238, 262, 286, 304], BUDA_LAMPS = [390, 406, 424, 440, 458, 474];
const TOPY = new Int16Array(W);                       // the top of the quay, column by column (the trees stand on it)
const ROADY = new Int16Array(W);                      // the quay road's row, column by column (cars and tram 2 run on it)
const TREES = [84, 93, 104, 113, 124, 137, 147];      // the bare trees of the park north of the Parliament
function renderLand() {
  const p = pix();
  const farWins = drawFar(p), pestWins = drawPest(p), parlWins = drawParliament(p);
  // quays: whatever lies between the buildings' feet and the water: a snowy parapet, the road, the stone wall
  for (let x = 0; x < W; x++) {
    const by = BANKY[x];
    let y = by - 1; while (y > 146 && !p.has(x, y)) y--;
    const top = y > 146 ? y + 1 : Math.min(by - 1, 152), n = by - top, pest = x < 312;
    const glow = pest ? clamp(1 - Math.abs(x - 228) / 110, 0, 1) : 0;                    // the Parliament's floodlights on its own embankment
    const upper = x < 100 || n < 6;                                                      // the road runs along the top there; elsewhere at the water's edge
    TOPY[x] = top; ROADY[x] = upper ? top + (n > 4 ? 1 : 0) : by - 2;
    for (let j = 0; j < n; j++) {
      const yy = top + j, h1 = hash(x, yy, 44);
      let col;
      if (j === n - 1) col = '#1c1a2c';
      else if (j === 0) col = h1 < 0.8 ? mixHex('#b4bedc', '#fff0c8', glow) : '#6c6880';
      else if (upper && j === 1 && n > 4) col = '#2a2838';
      else if (upper && j === 2 && n > 4) col = h1 < 0.7 ? '#8e98b8' : '#5a566c';
      else if (!upper && j === n - 2) col = '#2a2636';
      else if (!upper && j === n - 3) col = h1 < 0.75 ? mixHex('#8e98b8', '#e6d6ae', glow) : '#5a566c';
      else if (pest) col = glow > 0.05 ? pick(GOLD, 0.2 + glow * 0.42 - (j / n) * 0.12 + (x % 9 === 0 ? -0.14 : 0) + (j === 1 ? 0.08 : 0), x, yy)
        : pick(['#3a3444', '#5c4e50', '#84705c'], 0.4 - (j / n) * 0.3 + (x % 9 === 0 ? -0.2 : 0), x, yy);
      else col = pick(['#2a2840', '#3c3a54', '#545270'], 0.5 - (j / n) * 0.4 + (x % 8 === 0 ? -0.3 : 0), x, yy);
      p.put(x, yy, col);
    }
  }
  for (const lx of [...PEST_LAMPS, ...BUDA_LAMPS]) { p.put(lx, ROADY[lx] - 2, '#3a3448'); p.put(lx, ROADY[lx] - 3, '#3a3448'); p.put(lx, ROADY[lx] - 4, '#fff0c0'); }
  // bare winter trees, dark against the floodlit north end: a leaning trunk, a few limbs forking up and out at heights of
  // their own, a crown of fine twigs dithered over the light (the stone shows through it), snow lying in a fork or two
  const tr = rng(77), BARK = '#1c1620';
  const ink = (x, y, a, col = BARK) => {                                                 // col laid over what is there, at strength a
    if (x < 0 || y < 0 || x >= W || y >= H) return;
    const o = (y * W + x) * 4, h2 = v => v.toString(16).padStart(2, '0');
    p.put(x, y, p.has(x, y) ? mixHex('#' + h2(p.d[o]) + h2(p.d[o + 1]) + h2(p.d[o + 2]), col, a) : col);
  };
  const twig = (x, y, dx, len, a) => { for (let k = 1; k <= len; k++) ink(Math.round(x + dx * k), y - k, a); };   // dx: run per row up
  for (const tx of TREES) {
    const base = TOPY[tx], hgt = 10 + Math.floor(tr() * 7), lean = (tr() - 0.5) * 0.24, tip = j => Math.round(tx + lean * j);
    const side = tr() < 0.5 ? -1 : 1, cx = tx + lean * hgt * 0.7 + side * hgt * 0.06, cy = base - hgt * 0.66;
    const rx = hgt * (0.4 + tr() * 0.12), ry = hgt * 0.34, sk = 1 + side * 0.25;          // the crown leans to one side
    for (let y = Math.floor(cy - ry); y <= cy + ry; y++) for (let x = Math.floor(cx - rx * 1.3); x <= cx + rx * 1.3; x++) {
      const u = (x - cx) / (x < cx ? rx * (2 - sk) : rx * sk), q = u * u + ((y - cy) / ry) ** 2;
      if (q < 1 && (1 - q) * 0.95 > bayer(x, y) + (hash(x, y, tx) - 0.5) * 0.45) ink(x, y, 0.32);
    }
    const limbs = 3 + Math.floor(tr() * 2);
    for (let i = 0; i < limbs; i++) {
      const s = (i + (side > 0 ? 1 : 0)) % 2 ? 1 : -1, j0 = Math.round(hgt * (0.36 + 0.42 * (i + tr() * 0.7) / limbs));
      const dx = s * (0.5 + tr() * 0.8), len = Math.max(2, Math.round(hgt * (0.22 + tr() * 0.2))), x0 = tip(j0), y0 = base - j0;
      twig(x0, y0, dx, len, 0.85);
      const ex = x0 + dx * len, ey = y0 - len;                                           // it forks: one twig on out, one up
      twig(ex, ey, dx * 1.3, 1 + Math.floor(tr() * 2), 0.55); twig(ex, ey, dx * 0.2, 1 + Math.floor(tr() * 2), 0.55);
      if (i === 1 || tr() < 0.25) ink(Math.round(x0 + dx), y0 - 2, 0.75, '#c8d2ea');      // snow in the fork
    }
    for (let j = 0; j < hgt; j++) ink(tip(j), base - j, j < hgt * 0.62 ? 1 : 0.7);        // the trunk, its leader thinning
    if (hgt > 12) ink(tx + (lean > 0 ? -1 : 1), base, 0.8);                               // the root flare
  }
  const c = p.flush();
  SHOP_SIGN.y = BLOCK.base(30) - 13;
  sign(p.x, SHOP_SIGN.s, SHOP_SIGN.x, SHOP_SIGN.y, '#ff9a6a');
  // the mirror image: every column turned over at its own waterline, squashed, fading with depth
  const src = p.x.getImageData(0, 0, W, H).data, rh = RIV1 - RIV0, [rc, rx] = makeCanvas(W, rh), img = rx.createImageData(W, rh), d = img.data;
  for (let x = 0; x < W; x++) for (let y = BANKY[x]; y < RIV1; y++) {
    const k = y - BANKY[x], sy = BANKY[x] - 2 - Math.floor(k * 1.5);
    if (sy < 0) break;
    const o = (sy * W + x) * 4;
    if (!src[o + 3]) continue;
    const q = ((y - RIV0) * W + x) * 4, lum = (src[o] + src[o + 1] + src[o + 2]) / 765;
    d[q] = src[o]; d[q + 1] = src[o + 1]; d[q + 2] = src[o + 2];
    d[q + 3] = Math.round(255 * clamp((0.3 + lum * 0.62) * (1 - k / 62), 0, 1));
  }
  rx.putImageData(img, 0, 0);
  return { c: trim(c), refl: rc, wins: [...farWins, ...pestWins], parlWins };
}
function renderWater() {
  const h = RIV1 - RIV0, [c, x] = makeCanvas(W, h), img = x.createImageData(W, h), d = img.data;
  const P = ['#2c3158', '#22284c', '#191e3e', '#121630', '#0c0f24', '#090b1c'].map(hex);
  for (let y = 0; y < h; y++) for (let i = 0; i < W; i++) {
    const k0 = y + RIV0 - BANKY[i];                    // rows below this column's waterline
    const v = clamp(k0 / 46 + 0.06, 0, 1) * (P.length - 1), k = Math.floor(v);
    const col = P[Math.min(P.length - 1, k + (v - k > bayer(i, y) ? 1 : 0))], o = (y * W + i) * 4;
    d[o] = col[0]; d[o + 1] = col[1]; d[o + 2] = col[2]; d[o + 3] = 255;
  }
  x.putImageData(img, 0, 0);
  return c;
}

/* ================= Boats ================= */
/* a low sightseeing boat, facing right: white hull, a long lit glass saloon, bulbs along the open top deck */
const CRUISE_W = 66, CRUISE_H = 15;
function renderCruise(guests) {                       // guests: false leaves the saloon's tables empty (crowd 0)
  const p = pix(CRUISE_W, CRUISE_H), r = rng(88);
  for (let y = 10; y < 15; y++) {                                              // hull, raked bow on the right
    const x0 = 1 + (y > 12 ? y - 12 : 0), x1 = 63 - (y - 10) * 2;
    for (let x = x0; x <= x1; x++) p.put(x, y, y === 10 ? '#f2f4fa' : y === 14 ? '#16203c' : y === 13 ? '#2c4a7a' : x > x1 - 2 ? '#aab2c8' : '#d2d8e6');
  }
  for (let x = 6; x <= 53; x++) for (let y = 4; y < 10; y++) {                 // the glass saloon
    const m = (x - 6) % 6;
    p.put(x, y, y === 4 ? '#e6eaf4' : m === 0 ? '#3a3440' : y < 6 ? '#fff2c4' : y < 8 ? '#ffd88a' : '#e8a858');
  }
  for (let x = 8; guests && x < 52; x += 3) if (r() < 0.55) { const y = 7 + (r() < 0.5 ? 0 : 1); if ((x - 6) % 6) { p.put(x, y, '#3a2418'); p.put(x, y + 1, '#4a2e22'); } }   // guests at their tables
  for (let x = 10; x <= 48; x++) { if (x % 4 === 2) p.put(x, 3, '#8a90a4'); p.put(x, 2, x % 4 === 2 ? '#8a90a4' : '#5a6078'); }   // top deck rail
  for (let x = 12; x <= 46; x += 4) p.put(x, 1, '#ffe6a0');                    // bulbs
  p.rect(54, 3, 6, 7, '#dfe4f0'); p.rect(55, 5, 4, 2, '#ffd88a'); p.rect(54, 3, 6, 1, '#f6f8fc');   // wheelhouse
  p.put(57, 0, '#ffffff'); p.put(57, 1, '#5a6078'); p.put(57, 2, '#5a6078'); p.put(2, 9, '#fff4d8'); p.put(61, 9, '#6ae08a');
  return p.flush();
}
/* a Danube barge, facing right: long black hull low in the water, snow on the hatch covers, the wheelhouse aft */
const BARGE_W = 78, BARGE_H = 13;
function renderBarge() {
  const p = pix(BARGE_W, BARGE_H);
  for (let y = 7; y < 13; y++) {
    const x0 = 1 + (y > 10 ? y - 10 : 0), x1 = 76 - (y - 7);
    for (let x = x0; x <= x1; x++) p.put(x, y, y === 7 ? '#4a4c62' : y === 12 ? '#3e1c20' : y === 11 ? '#6a2a28' : '#15161f');
  }
  for (let x = 22; x <= 68; x++) { const seam = (x - 22) % 12 === 0; p.put(x, 5, seam ? '#6a7090' : hash(x, 1, 91) < 0.85 ? '#c2cbe6' : '#8e98b8'); p.put(x, 6, seam ? '#1c1d2a' : '#3a3e56'); }
  p.rect(5, 1, 12, 6, '#d6dae6'); p.rect(5, 1, 12, 1, '#f2f4fa'); p.rect(7, 3, 3, 2, '#ffd88a'); p.rect(12, 3, 3, 2, '#3a3c50');
  p.put(10, 0, '#5a6078'); p.put(10, -1, '#fff'); p.put(74, 6, '#6ae08a'); p.put(2, 6, '#fff4d8'); p.put(70, 5, '#5a6078'); p.put(70, 4, '#ffffff');
  return p.flush();
}
/* a hotel ship moored at the Pest quay by the park north of the Parliament: white, two decks of cabin lights */
const SHIP = { x0: 68, x1: 128 };
function renderShip() {
  const p = pix(), wins = [];
  for (let x = SHIP.x0; x <= SHIP.x1; x++) {
    const by = BANKY[(SHIP.x0 + SHIP.x1) >> 1] + 1, i = x - SHIP.x0, n = SHIP.x1 - SHIP.x0, bow = n - i, cabin = i > 4 && bow > 9;
    if (bow > 1 || i < 0) { p.put(x, by + 2, '#1a2444'); p.put(x, by + 1, '#b2bad2'); }
    p.put(x, by, bow < 3 ? '#b2bad2' : '#dfe4f2'); if (bow > 2) p.put(x, by - 1, '#f2f4fa');
    if (!cabin) continue;
    for (let y = by - 9; y < by - 1; y++) {
      const row = y - (by - 9);                         // 0 roof, 1..3 upper deck, 4 band, 5..7 lower deck
      let col = row === 0 ? '#f6f8fc' : row === 4 ? '#c2c8dc' : '#d6dbea';
      if ((row === 2 || row === 6) && i % 4 !== 0) col = hash(i >> 2, row, 92) < 0.66 ? (i % 4 === 1 ? '#ffe2a0' : '#ffc870') : '#39405c';
      p.put(x, y, col);
    }
    if (i % 4 === 1 && hash(i >> 2, 2, 92) < 0.66 && hash(i, 7, 92) < 0.5) wins.push({ id: 900 + i, x, y: by - 7, w: 3, h: 1, n: 3 + (i % 4), p: 0.55, off: hash(i, 8, 92) });
    if (i % 5 === 2) p.put(x, by - 10, '#ffe6a0');      // bulbs along the sun deck
    else p.put(x, by - 10, i % 5 === 0 ? '#8a90a8' : '#5a6078');
  }
  const mx = SHIP.x1 - 14; for (let y = BANKY[mx] - 13; y < BANKY[mx] - 9; y++) p.put(mx, y, '#8a90a8');
  p.put(mx, BANKY[mx] - 14, '#ff5a4a');
  return { c: trim(p.flush()), wins };
}

/* ================= River mist: ordered-dithered banks that drift and breathe ================= */
const FOG_DEFS = [
  { seed: 5, y: 154, h: 30, k: 1, dir: 1, top: '#7c84a8', bot: '#9a9cb8', alpha: 0.36, dens: t => 0.5 + 0.2 * wave(t, 3, 0.1) + 0.08 * wave(t, 7, 0.6) },
  { seed: 8, y: 174, h: 42, k: 2, dir: -1, top: '#7a84ac', bot: '#8e98bc', alpha: 0.44, dens: t => 0.46 + 0.22 * wave(t, 4, 0.35) + 0.08 * wave(t, 9, 0.2) },
];
const FOG_LEVELS = 6;
function makeFog(def) {
  const h = def.h, n = W * h, F = new Float32Array(n), A = hex(def.top), B = hex(def.bot);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const v = y / (h - 1), prof = Math.pow(Math.sin(Math.PI * Math.pow(v, 0.8)), 1.6);
    const nz = 0.48 + 0.34 * Math.sin(TAU * 2 * x / W + def.seed + y * 0.05) + 0.16 * Math.sin(TAU * 5 * x / W - y * 0.19 + def.seed * 2)
      + 0.1 * Math.sin(TAU * 11 * x / W + y * 0.41) + 0.06 * Math.sin(TAU * 23 * x / W - y * 0.7);
    F[y * W + x] = prof * clamp(nz, 0, 1);
  }
  const levels = [];
  for (let k = 0; k <= FOG_LEVELS; k++) {
    const D = k / FOG_LEVELS * 1.25, [c, cx] = makeCanvas(W, h), img = cx.createImageData(W, h), d = img.data;
    for (let q = 0; q < n; q++) {
      const x = q % W, y = (q / W) | 0, lv = Math.floor(Math.min(1, F[q] * D) * 4 + bayer(x, y)) / 4;
      if (lv <= 0) continue;
      const m = y / (h - 1), o = q * 4;
      d[o] = A[0] + (B[0] - A[0]) * m; d[o + 1] = A[1] + (B[1] - A[1]) * m; d[o + 2] = A[2] + (B[2] - A[2]) * m; d[o + 3] = Math.round(Math.min(1, lv) * 255);
    }
    cx.putImageData(img, 0, 0);
    levels.push(c);
  }
  return { ...def, levels };
}

/* ================= The bridge: railing, lamp posts, the snowy footway, the kerb, the near track, the wires ================= */
const RAILS = [251, 265];                             // the two rails of the Pest-bound track
const POOLS = [[LAMPS[0], 226, 44], [LAMPS[1], 226, 44], [CART.x + 22, 229, 32]];   // lamp light on the snow: x, y, radius
const SNOWR = ['#2a3150', '#3b4466', '#535e88', '#707ca8', '#929ec6'];
const WARMR = ['#5e5870', '#8e7c78', '#bc9c7c', '#e4c08c', '#fce0ac'];
const IRON = { d: '#12141f', m: '#1c2030', h: '#3d4564', w: '#7c6a54' };
const warmAt = (x, y) => { let w = 0; for (const [lx, ly, rr] of POOLS) w = Math.max(w, 1 - Math.hypot((x - lx) / rr, (y - ly) / (rr * 0.4))); return w; };
function renderBridge() {
  const p = pix();
  for (let y = WALK; y < KERB; y++) for (let x = 0; x < W; x++) {                // the footway: a trodden path through the snow
    const warm = warmAt(x, y), path = y >= 225 && y <= 236, h1 = hash(x, y, 51), h2 = hash(x >> 1, y, 52);
    let v = (path ? 0.4 : 0.68) + (h1 - 0.5) * 0.28 - (y === WALK ? 0.3 : 0);
    if (path && h2 < 0.09) v -= 0.3;                                              // footprints
    p.put(x, y, warm * 2.6 > bayer(x, y) + 0.08 ? pick(WARMR, v * 0.55 + warm * 0.7, x, y) : pick(SNOWR, v, x, y));
  }
  for (let y = KERB; y < H; y++) for (let x = 0; x < W; x++) {                    // kerb, then the track bed
    const h1 = hash(x, y, 53), warm = warmAt(x, y - 14) * 0.8, rail = RAILS.indexOf(y), groove = RAILS.indexOf(y - 1);
    let col;
    if (y === KERB) col = h1 < 0.86 ? (warm > 0.2 ? '#d8c4a4' : '#8894bc') : '#5a6488';
    else if (y < KERB + 4) col = [x % 23 === 0 ? '#2a2e40' : '#474c62', '#33374a', '#22253a'][y - KERB - 1];
    else if (rail >= 0) col = h1 < 0.8 ? (warm > 0.25 ? '#e2c492' : '#8b96bc') : '#525c82';                     // the rail's polished head
    else if (groove >= 0) col = '#07080e';
    else if (y > RAILS[0] + 1 && y < RAILS[1]) col = pick(['#1b1f30', '#2a3048', '#3d4666', '#566188', '#6c78a2'], 0.6 - Math.abs(y - 258.5) * 0.075 + (h1 - 0.5) * 0.4 + warm * 0.3 - (hash(x >> 3, y, 55) < 0.12 ? 0.3 : 0), x, y);   // snow packed between the rails
    else col = pick(['#10121c', '#1a1d2c', '#272c40', '#3a4260'], 0.36 + (h1 - 0.5) * 0.6 + warm * 0.4 + (y > RAILS[1] + 1 ? 0.2 : 0), x, y);
    p.put(x, y, col);
  }
  for (let x = 0; x < W; x++) {                                                   // the railing: handrail, posts, a lattice of iron scrolls
    const m = x % 16;
    p.put(x, RAIL - 1, hash(x, 1, 54) < 0.84 ? '#d2daf0' : '#8e9abe');
    p.put(x, RAIL, '#56607e'); p.put(x, RAIL + 1, IRON.d); p.put(x, WALK - 1, IRON.m);
    for (let y = RAIL + 2; y < WALK - 1; y++) {
      const dx = m - 2, dy = y - RAIL - 2;
      if (m < 2) p.put(x, y, m === 0 ? IRON.h : IRON.d);
      else if ((dx + dy) % 7 === 0 || (dx - dy + 14) % 7 === 0 || Math.abs(Math.hypot(dx - 6.5, dy - 4.5) - 3.3) < 0.55) p.put(x, y, dy < 2 ? '#39415f' : '#2a3049');
    }
  }
  for (const X of LAMPS) {                                                        // lamp posts on stone-and-iron pedestals
    for (let y = 201; y < WALK; y++) for (let x = X - 4; x <= X + 3; x++) p.put(x, y, y === 201 ? '#d2daf0' : y === 202 ? IRON.h : x === X - 4 ? IRON.h : x === X + 3 ? IRON.d : (y > 205 && y < 216 && x > X - 3 && x < X + 2) ? '#171a28' : IRON.m);
    for (let y = 166; y < 201; y++) { p.put(X - 1, y, y < 186 ? IRON.w : IRON.h); p.put(X, y, IRON.d); }
    for (const cy of [196, 184]) { p.rect(X - 2, cy, 4, 2, IRON.m); p.put(X - 2, cy, IRON.h); }
    p.rect(X - 6, 172, 12, 1, IRON.m); p.put(X - 7, 171, IRON.m); p.put(X + 6, 171, IRON.m);                  // the arms
    for (let k = 1; k <= 3; k++) { p.put(X - 1 - k, 172 + k, IRON.m); p.put(X + k, 172 + k, IRON.m); }         // scrolls under them
    for (const gx of [X - 8, X + 5]) {                                             // pendant globes
      p.rect(gx, 171, 3, 1, IRON.d); p.put(gx + 1, 170, '#d2daf0');
      p.rect(gx, 172, 3, 4, '#ffe2a0'); p.rect(gx, 172, 3, 2, '#fff6d8'); p.put(gx + 1, 176, '#c89048');
    }
    p.rect(X - 2, 161, 4, 5, '#ffe2a0'); p.rect(X - 2, 161, 4, 3, '#fff8e0'); p.rect(X - 2, 160, 4, 1, IRON.d);   // the centre lantern
    p.rect(X - 2, 159, 4, 1, '#d2daf0'); p.put(X - 1, 158, IRON.m); p.put(X - 1, 157, IRON.h);
    for (let k = 0; k < 6; k++) p.put(X + 1 + k, 181 + (k > 3 ? 1 : 0), IRON.m);                               // the catenary holder
    p.put(X + 7, 183, '#8a94b8'); p.put(X + 7, 184, IRON.m);
  }
  // the overhead line: messenger wire slung from the posts' holders, droppers, the contact wire
  for (let x = 0; x < W; x++) {
    const s = ((x - LAMPS[0] - 7) % 256 + 256) % 256 / 256, my = Math.round(184 + 4 * 4 * s * (1 - s) * 0.8);
    p.put(x, my, '#1a1c2c'); p.put(x, WIRE, '#0c0d18');
    if (x % 32 === 11) for (let y = my + 1; y < WIRE; y++) p.put(x, y, '#1a1c2c');
  }
  return p.flush();
}
/* the chestnut cart, a layer of its own (the seller stands behind it) */
function renderCart() {
  const q = pix();
  // wheels, a green box with a roasting pan on it, a striped awning with its board
  const X = CART.x, Y = CART.y;
  for (const wx of [X + 6, X + 28]) for (let j = -4; j <= 4; j++) for (let i = -4; i <= 4; i++) {
    const d = Math.hypot(i, j);
    if (d < 4.4) q.put(wx + i, Y - 1 + j, d > 3.2 ? '#0e0f18' : d < 1.2 ? '#7a6248' : (i === 0 || j === 0) ? '#3a3040' : (Math.abs(i) === Math.abs(j) ? '#2a2432' : null) || '#191a26');
  }
  for (let y = Y - 15; y < Y - 3; y++) for (let x = X + 1; x <= X + 33; x++) q.put(x, y, y === Y - 15 ? '#c89a4a' : y === Y - 4 ? '#0f2218' : (x - X) % 8 === 1 ? '#15301f' : y === Y - 14 ? '#3a6a4c' : '#1f3d2e');
  q.rect(X, Y - 16, 35, 1, '#7a5632');
  q.rect(X + 3, Y - 20, 11, 4, '#101018'); q.rect(X + 3, Y - 20, 11, 1, '#3a3440'); q.rect(X + 2, Y - 17, 13, 1, '#2a2632');     // the pan
  for (const [i, c2] of [[4, '#5a3418'], [6, '#7a4a22'], [8, '#4a2a14'], [10, '#6a3e1c'], [12, '#5a3418']]) q.put(X + i, Y - 21, c2);
  for (let k = 0; k < 3; k++) { q.put(X + 18 + k * 2, Y - 17, '#e6dcc0'); q.put(X + 18 + k * 2, Y - 18, '#f4ecd4'); q.put(X + 18 + k * 2, Y - 19, '#fffaf0'); }   // paper cones
  for (const px0 of [X + 1, X + 33]) for (let y = Y - 36; y < Y - 15; y++) q.put(px0, y, IRON.m);
  for (let y = Y - 41; y < Y - 36; y++) for (let x = X - 3; x <= X + 37; x++) q.put(x, y, y === Y - 41 ? '#d2daf0' : Math.floor((x - X + 3) / 3) % 2 ? '#a83a30' : '#e8dcc0');
  for (let y = Y - 36; y < Y - 29; y++) for (let x = X - 2; x <= X + 36; x++) q.put(x, y, y === Y - 30 ? '#8a7a5a' : '#eadcb8');
  q.put(X + 30, Y - 28, IRON.m); q.put(X + 30, Y - 27, IRON.d); q.put(X + 30, Y - 26, '#fff4d0'); q.put(X + 30, Y - 25, '#ffd68a');   // its lamp
  const c = q.flush();
  sign(q.x, 'GESZTENYE', X - 1, Y - 35, '#8a2420');
  return trim(c);
}

/* ================= The roof sign on the Pest block (the city's billboard) =================
   A steel frame on the block's roof, three lamps on arms over its top edge, snow along the top. The city's own ad:
   HOLD FURDO (with its accents), "Moon Baths", a made-up thermal bath open at night: a steaming turquoise pool under a
   crescent moon, two bathers up to their necks. The steam is drawn per frame. */
function renderBillboard() {
  const p = pix(), L = BILL.x - 2, Rr = BILL.x + BILL.w + 1, T = BILL.y - 2, B = BILL.y + BILL.h + 1;
  const legs = [L + 4, (L + Rr) >> 1, Rr - 4];
  for (let k = 0; k + 1 < legs.length; k++) { line(p, legs[k], B + 1, legs[k + 1], BLOCK.top(legs[k + 1]) - 6, '#191b2e'); line(p, legs[k + 1], B + 1, legs[k], BLOCK.top(legs[k]) - 6, '#191b2e'); }
  for (const lx of legs) for (let y = B + 1; y < BLOCK.top(lx) - 5; y++) p.put(lx, y, y === B + 1 ? '#3a4060' : '#10121e');
  for (let y = T; y <= B; y++) for (let x = L; x <= Rr; x++) p.put(x, y, x === L || y === T ? '#3a4060' : x === Rr || y === B ? '#0c0d18' : '#161828');
  for (let x = L; x <= Rr; x++) if (hash(x, 3, 61) < 0.86) p.put(x, T - 1, hash(x, 4, 61) < 0.7 ? '#dfe6f8' : '#a6b0d2');
  for (const lx of BILL_LAMPS) {
    p.put(lx, T - 2, '#10121e'); p.put(lx, T - 3, '#10121e'); p.rect(lx - 2, BILL_LAMP_Y - 1, 5, 1, '#10121e'); p.put(lx - 2, BILL_LAMP_Y - 2, '#c8d2ea'); p.put(lx - 1, BILL_LAMP_Y - 2, '#dfe6f8');
    p.rect(lx - 2, BILL_LAMP_Y, 5, 1, '#ffe2a0'); p.rect(lx - 1, BILL_LAMP_Y, 3, 1, '#fff6d8');
  }
  p.rect(BILL.x, BILL.y, BILL.w, BILL.h, '#0d0f24');
  return trim(p.flush());
}
const AD_STEAM = [[8, 0], [16, 0.37], [22, 0.71], [30, 0.2]];
function renderAd() {
  const { w, h } = BILL, p = pix(w, h);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    let col = pick(['#0b1430', '#102044', '#17305c', '#1e4270'], y < 8 ? 0.1 + y * 0.03 : 0.25 + (y - 8) * 0.08, x, y);
    if (y >= 14 && y <= 17) col = y === 14 ? (x % 5 === 2 ? '#d6fff4' : '#8cf0de') : pick(['#1c8a8c', '#2cb4ac', '#4cd8c8'], 0.7 - (y - 15) * 0.3 + Math.sin(x * 0.9) * 0.2, x, y);
    else if (y === 13 && (x < 3 || x > w - 4)) col = '#b8a888';
    else if (y === 18) col = '#7a6a58';
    p.put(x, y, col);
  }
  for (const [bx, hair] of [[11, '#3a2418'], [26, '#d8b060']]) { p.put(bx, 12, hair); p.put(bx + 1, 12, hair); p.put(bx, 13, '#f0c8a0'); p.put(bx + 1, 13, '#e0b088'); }
  for (const [i, j] of [[35, 8], [36, 8], [34, 9], [34, 10], [35, 11], [36, 11]]) p.put(i, j, '#fff2b0');   // crescent moon
  p.put(37, 11, '#d8c880');
  const c = p.flush();
  sign(p.x, 'HOLD FuRDo', 2, 2, '#ffe070');
  return c;
}

/* ================= The tram: a Combino of lines 4 and 6, 54 m of yellow low-floor tram heading for Pest (left) ================= */
const TRAM_W = 756, PANTO = 32, MOD = 126;            // sprite: PANTO rows for the pantograph over TRAM_H rows of body; a section's pitch
const PANTO_X = 2 + 2 * MOD + 62;                     // the pantograph's head, on the third section
const SEATS = [];                                     // passengers: { x, stand, door, col, rank }
const LED = ['#f4faff', '#f4faff', '#f4faff', '#9cd4ff', '#9cd4ff', '#ffe9a8'];
function renderTram(light) {
  const p = pix(TRAM_W, PANTO + TRAM_H), T = PANTO, r = rng(4646);
  const dim = c => light ? mixHex(c, '#0a0e22', 0.58) : c;
  const Y = { hi: dim('#ffd94a'), m: dim('#f0be14'), s: dim('#c6940c') }, K = dim('#16171f'), ROOF = [dim('#d6dae6'), dim('#c2c7d6'), dim('#a6acc0')];
  for (let x = 0; x < TRAM_W; x++) {
    const sec = clamp(Math.floor((x - 2) / MOD), 0, 5), m0 = x - 2 - sec * MOD, m = sec === 5 ? 121 - m0 : m0;   // the last section mirrors the first
    const end = x < 14 ? x : x >= TRAM_W - 14 ? TRAM_W - 1 - x : 99, cut = end < 14 ? Math.max(5, Math.round(33 - end * 2.3)) : 0;   // the sloping ends
    const cab = (sec === 0 || sec === 5) && m < 34, bellows = m0 > 121 && sec < 5;
    const door = !cab && m >= 37 && m <= 64, win = !cab && ((m >= 4 && m <= 33) || (m >= 68 && m <= 97) || (m >= 102 && m <= 117));
    for (let j = 0; j < TRAM_H; j++) {
      if (j < cut || (x < 2 || x >= TRAM_W - 2) && j < 28) continue;
      let col = null;
      if (bellows) col = j < 5 ? null : j > 45 ? '#0a0b12' : (m0 & 1) ? '#22242e' : '#15161d';
      else if (j < 4) { if (!cab && ((m > 16 && m < 58) || (m > 76 && m < 112)) && end > 20) col = j === 0 ? dim('#727a92') : dim('#4c5266'); }
      else if (j === 4) col = dim('#8a90a6');
      else if (j < 8) col = ROOF[j - 5];
      else if (j >= 46) col = '#0a0b12';
      else if (j >= 42) col = j === 42 ? dim('#34374a') : m % 40 < 14 ? '#14151e' : dim('#262836');
      else if (cab) {
        if (end < 14 && j < 30) col = j - cut < 2 ? dim('#8a90a6') : (j + end) % 9 === 0 ? '#52648e' : '#27324e';        // windscreen
        else if (m >= 16 && m <= 31 && j >= 12 && j < 30) col = j < 14 ? '#5a6890' : '#2c3654';                          // the cab's side window
        else col = j === 8 || j === 31 ? Y.hi : j > 39 ? Y.s : Y.m;
      } else if (door) {
        const dm = m - 37;
        if (j < 9) col = Y.m;
        else if (dm === 0 || dm === 27 || dm === 13 || dm === 14 || j === 9 || j > 39) col = dm === 13 ? '#3a3320' : Y.s;
        else col = j < 12 ? '#fffbe6' : j < 24 ? '#ffeec0' : j < 34 ? '#f0d8a4' : '#c8aa78';
      } else if (j >= 11 && j < 30) {
        if (!win) col = K;
        else {
          const wm = m >= 102 ? m - 102 : m >= 68 ? m - 68 : m - 4;
          col = j === 11 ? '#fffbe6' : j < 15 ? '#fff0c4' : j < 23 ? '#f6dca4' : '#dcc08c';
          if (wm % 15 === 7 && j > 11) col = '#d8a41c';                                                                 // a grab pole
          if (j >= 25 && wm % 8 >= 2 && wm % 8 <= 6) col = '#39446a';                                                    // seat backs
        }
      } else col = j === 8 || j === 31 ? Y.hi : j === 10 || j === 30 ? K : j > 39 ? Y.s : Y.m;
      if (col) p.put(x, T + j, col);
    }
  }
  for (const [x0, col] of [[1, '#ffffff'], [TRAM_W - 4, '#ff4a3a']]) p.rect(x0, T + 36, 3, 2, col);        // head lamps, tail lamps
  p.rect(20, T + 17, 4, 4, '#1c1a26'); p.rect(19, T + 21, 6, 9, '#1c1a26');                                   // the driver
  for (let k = 0; k <= 14; k++) { p.put(PANTO_X - 14 + k, T - k, '#2a2c3a'); p.put(PANTO_X - 13 + k, T - k, '#3e4258'); }   // pantograph: lower arm,
  for (let k = 0; k <= 15; k++) p.put(PANTO_X - Math.round(k * 0.2), T - 14 - k, '#3e4258');                                   // upper arm,
  p.rect(PANTO_X - 9, 1, 13, 1, '#4c5268'); p.rect(PANTO_X - 8, 2, 11, 1, '#1e2030'); p.rect(PANTO_X - 22, T - 1, 16, 1, '#3e4258');   // collector
  if (light) {                                         // the light tram: every edge traced in small white and ice-blue lamps
    const led = (x, y) => p.put(x, T + y, LED[Math.floor(r() * LED.length)]);
    for (let x = 14; x < TRAM_W - 14; x++) {
      const sec = Math.floor((x - 2) / MOD), m0 = x - 2 - sec * MOD;
      if (x % 2 === 0) { led(x, 5); led(x, 42); }
      if (x % 2 === 1) { led(x, 10); led(x, 30); }
      const sw = 32 + Math.round(8 * Math.abs(Math.sin(Math.PI * (m0 % 30) / 30)));
      if (m0 <= 121 && !(m0 >= 37 && m0 <= 64)) led(x, sw);
      if ([2, 35, 66, 99, 119].includes(m0)) for (let y = 12; y < 30; y += 2) led(x, y);
      if ([37, 64].includes(m0)) for (let y = 9; y < 42; y += 2) led(x, y);
    }
    for (let k = 0; k < 14; k++) { led(k, Math.max(6, Math.round(33 - k * 2.3)) - 1); led(TRAM_W - 1 - k, Math.max(6, Math.round(33 - k * 2.3)) - 1); }
  }
  return p.flush();
}
{ const r = rng(4647);                                 // who rides: seated by the windows, standing by the doors
  const COL = ['#3a2a2e', '#2a2e44', '#44343a', '#2e3a34', '#3c3048', '#4a3c30'];
  for (let sec = 0; sec < 6; sec++) for (let m = 6; m < 118; m += 8) {
    const cabSide = (sec === 0 && m < 36) || (sec === 5 && m > 86);
    if (cabSide || (m > 32 && m < 40) || (m > 62 && m < 70) || (m > 96 && m < 104)) continue;
    const door = m >= 37 && m <= 64;
    if (r() < (door ? 0.4 : 0.55)) SEATS.push({ x: 2 + sec * MOD + m, stand: door || r() < 0.2, door, col: COL[Math.floor(r() * COL.length)], hair: r() < 0.3, rank: 0 });
  }
  SEATS.forEach((s, i) => { s.rank = frac((i + 0.5) * 0.6180339887); });
}

/* ================= People ================= */
const SKINS = ['#f2c8a0', '#e2ac82', '#c48c60', '#8c5a3c'], HAIRS = ['#1c1412', '#3c2418', '#6c4222', '#d0a858', '#96421c', '#d4d0cc'];
const NIGHT = '#141830', GLOWC = '#ffdf9c';
/** a look: the proportions for a height of h px and its colours dimmed for the night (dk). Winter things: coat (rows
    of coat over the legs), scarf, hat (a knitted cap), bag, apron; long: rows of long hair. */
function look(h, skin, hair, top, bot, o = {}) {
  const dk = o.dk ?? 0.3, m = c => mixHex(c, NIGHT, dk);
  const hh = h >= 22 ? 5 : 4, th = Math.round(h * 0.38);
  return { h, hh, hw: h >= 19 ? 4 : 3, th, tw: h >= 22 ? 7 : h >= 19 ? 6 : 5, ll: h - hh - th,
    skin: m(SKINS[skin]), hair: m(HAIRS[hair]), hairL: mixHex(HAIRS[hair], GLOWC, 0.35), top: m(top), topD: mixHex(top, NIGHT, dk + 0.28),
    topL: mixHex(top, GLOWC, 0.42), bot: m(bot), shoe: m('#1a1418'), long: o.long || 0, coat: o.coat || 0,
    hat: o.hat ? m(o.hat) : null, hatL: o.hat ? mixHex(o.hat, GLOWC, 0.35) : null, scarf: o.scarf ? m(o.scarf) : null, bag: o.bag ? m(o.bag) : null, apron: !!o.apron };
}
/**
 * One person, feet on row fy, centred on column x. Returns the row of the top of the head.
 * o.view: 0 from behind (looking at the river), 1 facing us, 2 in profile (o.dir: 1 right, -1 left)
 * o.step: stride frame (profile), o.kneel: crouched, o.lean: head px towards a neighbour, o.cup: a paper cup in the hand
 * o.aL / o.aR: 0 down, 1 raised and pointing, 2 bent up with a phone, 3 out, 4 bent (holding), 6 hand to the mouth
 */
function person(x, fy, P, o = {}) {
  x = Math.round(x); fy = Math.round(fy);
  const { hw, hh, tw, th } = P, view = o.view || 0, dir = o.dir || 1, side = view === 2;
  const legs = o.kneel ? (P.ll >> 1) : P.ll, ty = fy - legs - th + 1, hy = ty - hh, bw = side ? tw - 2 : tw, tx = x - (bw >> 1), ly = fy - legs + 1;
  R(tx - 1, fy + 1, bw + 3, 1, 'rgba(10,12,30,0.3)');                                    // a shadow on the snow
  if (side) {
    if (o.kneel) { R(x - 1, ly, 3, legs, P.bot); R(dir > 0 ? x + 1 : x - 3, fy, 3, 1, P.shoe); }
    else if (o.step) {
      R(dir > 0 ? x : x - 1, ly, 2, legs - 1, P.bot); R(dir > 0 ? x + 1 : x - 2, fy, 2, 1, P.shoe);
      R(dir > 0 ? x - 2 : x + 1, ly, 2, legs - 2, P.bot); R(dir > 0 ? x - 3 : x + 2, fy - 1, 2, 1, P.shoe);
    } else { R(x - 1, ly, 2, legs, P.bot); R(dir > 0 ? x - 1 : x - 2, fy, 3, 1, P.shoe); }
  } else {
    const lw = bw >= 7 ? 3 : 2;
    R(tx, ly, bw, 2, P.bot); R(tx, ly + 2, lw, legs - 2, P.bot); R(tx + bw - lw, ly + 2, lw, legs - 2, P.bot);
    R(tx, fy, lw, 1, P.shoe); R(tx + bw - lw, fy, lw, 1, P.shoe);
  }
  if (P.coat && legs > 3) { const cl = Math.min(P.coat, legs - 2); R(tx, ly, bw, cl, P.top); if (!side) R(tx + bw - 1, ly, 1, cl, P.topD); }
  R(tx, ty, bw, th, P.top);
  if (!side) R(tx + bw - 1, ty + 1, 1, th - 1, P.topD);
  if (view !== 1) R(tx, ty, bw, 1, P.topL);                                               // the lamps' light on the shoulders
  if (P.apron && view === 1) R(tx + 1, ty + 3, bw - 2, th - 3, '#bdb6aa');
  if (P.bag) { if (view === 0) R(tx + 1, ty + 2, bw - 2, th - 3, P.bag); else if (side) R(dir > 0 ? tx - 1 : tx + bw - 1, ty + 1, 2, th - 2, P.bag); }
  const cup = (cx, cy) => { if (o.cup) { R(cx, cy - 1, 1, 2, '#e8e0cc'); R(cx, cy - 1, 1, 1, '#fff8e8'); } };
  if (side) {
    const st = o.aR || 0;
    if (st === 0) { const ax = x + (o.step ? -dir : 0); R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
    else if (st === 1) { for (let k = 0; k < 4; k++) R(x + dir * (1 + k), ty + 1 - k, 1, 1, k < 3 ? P.topD : P.skin); }
    else if (st === 2) { for (let k = 0; k < 3; k++) R(x + dir * (1 + k), ty + 1 - k, 1, 1, P.topD); R(x + dir * 4 - (dir < 0 ? 1 : 0), ty - 3, 2, 3, '#0e1018'); }
    else if (st === 3) { R(dir > 0 ? x + 1 : x - 3, ty + 2, 3, 1, P.topD); R(x + dir * 4, ty + 2, 1, 1, P.skin); cup(x + dir * 4, ty + 1); }
    else if (st === 4) { R(x + dir, ty + 2, 1, 2, P.topD); R(x + dir * 2, ty + 3, 1, 1, P.skin); cup(x + dir * 2, ty + 2); }
    else { R(x + dir, ty + 1, 1, 2, P.topD); R(x + dir * 2, ty, 1, 1, P.skin); cup(x + dir * 2, ty - 1); }
  } else {
    const arm = (ax, st, sg) => {
      if (!st) { R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
      else if (st === 1) { for (let k = 0; k < 4; k++) R(ax + sg * k, ty + 1 - k, 1, 1, k < 3 ? P.topD : P.skin); }
      else if (st === 2) { R(ax, hy + 2, 1, ty - hy, P.topD); R(ax, hy + 1, 1, 1, P.skin); const p0 = sg < 0 ? ax - 1 : ax; R(p0, hy - 2, 2, 3, '#0e1018'); if (view === 0) { R(p0, hy - 2, 2, 1, '#ffe2a8'); R(p0, hy - 1, 2, 1, '#8ab8e8'); } }
      else if (st === 3) { R(sg < 0 ? ax - 2 : ax, ty + 1, 3, 1, P.topD); R(sg < 0 ? ax - 3 : ax + 3, ty + 1, 1, 1, P.skin); }
      else { R(ax, ty + 1, 1, 2, P.topD); R(ax + (sg < 0 ? 1 : -1), ty + 3, 1, 1, P.skin); }
    };
    arm(tx - 1, o.aL, -1); arm(tx + bw, o.aR, 1);
  }
  const hx = x - (hw >> 1) + (o.lean || 0);
  if (view === 0) {
    R(hx, hy, hw, hh, P.hair); R(hx, hy, hw, 1, P.hairL);
    if (P.long) R(hx, hy + hh, hw, P.long, P.hair); else R(hx + 1, hy + hh - 1, hw - 2, 1, P.skin);
  } else if (view === 1) {
    R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, 1, P.hair);
    if (P.long) { R(hx - 1, hy + 1, 1, hh + P.long - 1, P.hair); R(hx + hw, hy + 1, 1, hh + P.long - 1, P.hair); }
  } else { R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, 1, P.hair); R(dir > 0 ? hx : hx + hw - 1, hy, 1, hh - 1 + P.long, P.hair); }
  if (P.scarf) {
    if (side) { R(hx, hy + hh, hw, 1, P.scarf); R(dir > 0 ? hx - 1 : hx + hw, hy + hh, 1, 4, P.scarf); }
    else { R(tx + 1, ty, bw - 2, 1, P.scarf); R(x + 1, ty + 1, 1, 3, P.scarf); }
  }
  if (P.hat) { R(hx, hy - 1, hw, 2, P.hat); R(hx, hy - 1, hw, 1, P.hatL); R(hx + (hw >> 1), hy - 2, 1, 1, P.hatL); }
  return hy;
}
const COATS = ['#8a3434', '#2f4a72', '#5c5c66', '#b89a68', '#35593f', '#6a4878', '#c8c0b0', '#22242e', '#b0642a', '#3f7482'];
const TROUS = ['#2a3046', '#1e1e28', '#3a3430', '#44506a'];
const WHO = {
  he: look(20, 1, 1, COATS[1], TROUS[1], { coat: 3, scarf: '#c8c0b0' }),
  she: look(19, 0, 3, COATS[0], TROUS[0], { coat: 5, long: 2, hat: '#e8e0d0' }),
  photo: look(20, 0, 0, COATS[7], TROUS[2], { hat: '#3f7482', bag: '#5a4a3a', dk: 0.2 }),
  parent: look(20, 2, 0, COATS[4], TROUS[1], { coat: 4, scarf: '#b0642a' }),
  child: look(12, 0, 3, '#d8b040', TROUS[0], { hat: '#c84a4a', dk: 0.25 }),
  friendA: look(21, 0, 2, COATS[3], TROUS[0], { coat: 4, hat: '#6a4878' }),
  friendB: look(20, 1, 0, COATS[5], TROUS[1], { coat: 3, long: 2, scarf: '#e8d8a0' }),
  vendor: look(21, 1, 5, '#5a4638', TROUS[2], { hat: '#2a2a34', apron: true, dk: 0.1 }),
  customer: look(21, 0, 4, COATS[9], TROUS[0], { coat: 4, long: 2, dk: 0.2 }),
  leaner: look(20, 1, 5, COATS[2], TROUS[1], { coat: 3, hat: '#3a3430' }),
  filmer: look(19, 0, 1, COATS[6], TROUS[3], { coat: 4, long: 3 }),
  coat: look(24, 0, 2, COATS[3], TROUS[1], { coat: 8, scarf: '#a83a30', dk: 0.25 }),
  shopper: look(23, 2, 0, COATS[5], TROUS[0], { coat: 4, hat: '#c8c0b0', dk: 0.25 }),
  cyc: look(23, 0, 1, COATS[8], TROUS[3], { hat: '#2f4a72', dk: 0.25 }),
  armA: look(23, 1, 0, COATS[7], TROUS[1], { coat: 5, scarf: '#35593f', dk: 0.25 }),
  armB: look(22, 0, 4, COATS[0], TROUS[2], { coat: 6, long: 2, hat: '#e8e0d0', dk: 0.25 }),
  dog: look(24, 0, 5, COATS[1], TROUS[2], { coat: 4, hat: '#5c5c66', dk: 0.25 }),
};
// the people who stay (x, feet row, rank); the couple, the photographer and the chestnut seller are the cast (rank 0)
const AT = {
  he: { x: 228, y: 223, rank: 0 }, she: { x: 235, y: 223, rank: 0 }, photo: { x: 131, y: 224, rank: 0 }, vendor: { x: CART.x + 24, y: 229, rank: 0 },
  parent: { x: 84, y: 223, rank: 0.2 }, child: { x: 91, y: 223, rank: 0.2 }, friendA: { x: 345, y: 226, rank: 0.4 }, friendB: { x: 355, y: 226, rank: 0.4 },
  leaner: { x: 272, y: 223, rank: 0.6 }, customer: { x: CART.x - 8, y: 231, rank: 0.65 }, filmer: { x: 168, y: 223, rank: 0.8 },
};
// the people who walk through: [from, to, direction] windows; `mate` walks arm in arm, `bike` is pushed, `dog` trots ahead
const WALKS = [
  { who: 'coat', rank: 0.1, y: 237, pass: [[56, 86, -1], [176, 206, 1]] },
  { who: 'shopper', rank: 0.45, y: 232, pass: [[22, 52, 1], [140, 170, -1]], carry: true },
  { who: 'cyc', rank: 0.55, y: 237, pass: [[100, 132, 1]], bike: true },
  { who: 'armA', mate: 'armB', rank: 0.7, y: 232, pass: [[66, 100, 1], [186, 220, -1]] },
  { who: 'dog', rank: 0.9, y: 236, pass: [[118, 150, -1], [206, 236, 1]], dog: true },
];
const BIKE = ['..........HH............', '.........H..............', '....SS...H..............', '.....F...F..............', '.....FFFFFFF............',
  '..WWWF.....FF...WWW.....', '.W...FW.....F..W...W....', 'W....FFW....FFW..F..W...', 'W..FF..W.....FW.F...W...', 'W......W......W.....W...',
  '.W....W........W...W....', '..WWWW..........WWW.....'];
const BIKE_COL = { W: '#0c0d16', F: '#8a3030', H: '#2a2c3a', S: '#1a1418' };
const DOGS = [['......HH', 'T.BBBBHH', '.BBBBBB.', '.B....B.', '.L....L.'], ['......HH', 'T.BBBBHH', '.BBBBBB.', '..B..B..', '.L....L.']];
const DOG_COL = { B: '#3a2a22', H: '#4a3628', T: '#3a2a22', L: '#241a16' };
function sprite(rows, pal, flip) {
  const w = rows[0].length, [c, x] = makeCanvas(w, rows.length);
  rows.forEach((row, j) => { for (let i = 0; i < w; i++) if (pal[row[i]]) { x.fillStyle = pal[row[i]]; x.fillRect(flip ? w - 1 - i : i, j, 1, 1); } });
  return c;
}

/* ================= Gulls in the floodlights, smoke, snow ================= */
const FLAP = [['1...1', '.1.1.', '..2..'], ['.....', '11.11', '..2..'], ['.....', '.121.', '1...1']];
const GULLS = Array.from({ length: 9 }, (_, i) => {
  const r = rng(300 + i);
  return { i, k: 5 + Math.floor(r() * 5), ph: r(), rx: 12 + r() * 36, ry: 4 + r() * 11, cy: 74 + r() * 24, dir: r() < 0.5 ? 1 : -1, k2: 7 + Math.floor(r() * 9) };
});
const SMOKE = Array.from({ length: 14 }, (_, i) => { const r = rng(610 + i); return { n: 14 + Math.floor(r() * 6), ph: r(), k: 9 + Math.floor(r() * 12), p: r() }; });
const STEAM = Array.from({ length: 9 }, (_, i) => { const r = rng(640 + i); return { n: 40 + Math.floor(r() * 16), ph: r(), k: 20 + Math.floor(r() * 20), p: r() }; });
const SNOW = [
  { count: 150, nMin: 12, nMax: 18, size: 1, col: '#97a8d0', a: 0.62, amp: 2.5, drift: -8, lit: false, land: [146, 158] },
  { count: 100, nMin: 18, nMax: 26, size: 1, col: '#dfe8fa', a: 0.9, amp: 4.5, drift: -16, lit: true, land: [172, 206] },
  { count: 44, nMin: 26, nMax: 36, size: 2, col: '#f2f6ff', a: 0.95, amp: 8, drift: -26, lit: true, land: [H + 4, H + 4] },
];
SNOW.forEach((L, li) => {
  const r = rng(7700 + li);
  L.flakes = Array.from({ length: L.count }, (_, i) => ({ id: i + li * 1000, n: L.nMin + Math.floor(r() * (L.nMax - L.nMin + 1)), ph: r(), k: 14 + Math.floor(r() * 40), p: r() }));
});
// where a flake catches the light: the bridge's lamps, the cart, and the whole floodlit Parliament (flakes go gold before it)
const LIGHTS = [
  { x: LAMPS[0], y: LAMP_Y + 6, rx: 28, ry: 24, col: '#ffe2ac' }, { x: LAMPS[1], y: LAMP_Y + 6, rx: 28, ry: 24, col: '#ffe2ac' },
  { x: CART.x + 26, y: 212, rx: 20, ry: 16, col: '#ffd898' }, { x: 186, y: 128, rx: 112, ry: 34, col: '#ffe6b4' },
];
const LIGHTS_BB = [{ x: BILL.x + (BILL.w >> 1), y: BILL.y + 6, rx: 30, ry: 16, col: '#fff0cc' }, ...LIGHTS];   // + the roof sign's lamps
// a picked weather (site/js/engine/weather.js): Budapest draws its own snow; the snow lying on the bridge, the quays and the
// roofs stays under every pick. Rain and leaves land behind the Parliament, on the river and past the bottom of the frame.
const WEATHER = weatherPlans(['snow']);
const WX_LAND = [[146, 158], [172, 206], [H + 4, H + 4]];
const WX = { land: WX_LAND, lights: LIGHTS }, WX_BB = { land: WX_LAND, lights: LIGHTS_BB };
function litAt(x, y, lights) {
  for (const l of lights) { const dx = (x - l.x) / l.rx, dy = (y - l.y) / l.ry; if (dx * dx + dy * dy < 1) return l.col; }
  return null;
}
function drawSnow(L, t, lights) {
  const band = L.land, span = W + 40, fades = band[0] < H;
  for (const f of L.flakes) {
    const { cyc, u } = cycle(t, f.n, f.ph);
    const yEnd = lerp(band[0], band[1], hash(f.id, cyc, 2)), y = -4 + u * (yEnd + 4);
    let x = hash(f.id, cyc, 1) * span + u * L.drift + L.amp * Math.sin(TAU * (f.k * t / LOOP + f.p));
    x = ((x % span) + span) % span - 20;
    const lit = L.lit ? litAt(x, y, lights) : null;
    ctx.fillStyle = lit || L.col; ctx.globalAlpha = (lit ? 0.98 : L.a) * (fades && u > 0.88 ? (1 - u) / 0.12 : 1);
    ctx.fillRect(Math.round(x), Math.round(y), L.size, L.size);
  }
  ctx.globalAlpha = 1;
}
const STREAKS = [
  ...[166, 184, 204, 224, 245, 266, 288].map((x, i) => ({ x, w: 4, col: '#ffd070', a: 0.24, seed: 1 + i, len: 40 })),
  ...PEST_LAMPS.map((x, i) => ({ x, w: 2, col: '#ffd890', a: 0.3, seed: 10 + i, len: 22 })),
  ...BUDA_LAMPS.map((x, i) => ({ x, w: 2, col: '#ffd890', a: 0.3, seed: 24 + i, len: 22 })),
  ...CHAIN.t.map((x, i) => ({ x, w: 3, col: '#ffe8b0', a: 0.24, seed: 32 + i, len: 24 })),
  { x: PALACE.dome, w: 5, col: '#ffe0a0', a: 0.1, seed: 36, len: 26 }, { x: 26, w: 16, col: '#ffb060', a: 0.12, seed: 37, len: 26 },
];
// cars on the quay roads: [from, to] columns, passes a loop, phase, direction
const CARS = [];
{ const r = rng(515);
  for (let i = 0; i < 9; i++) CARS.push({ x0: 100, x1: 306, n: 6 + Math.floor(r() * 5), ph: r(), dir: i % 2 ? 1 : -1, id: i });
  for (let i = 0; i < 5; i++) CARS.push({ x0: 386, x1: 480, n: 8 + Math.floor(r() * 5), ph: r(), dir: i % 2 ? 1 : -1, id: 20 + i });
  for (let i = 0; i < 4; i++) CARS.push({ x0: -4, x1: 80, n: 9 + Math.floor(r() * 5), ph: r(), dir: i % 2 ? 1 : -1, id: 30 + i, big: true });
}
const FLAG_ROWS = ['#d8443a', '#f2efe6', '#3f8a52'];

/* ================= Pre-render everything static ================= */
let HL, SKY_C, MOON_C, CLOUDS, LAND, WATER_C, CRUISE_C, BARGE_C, SHIP_C, FOGS, BRIDGE_C, CART_C, BILL_C, AD_C, PITCH, TRAM_C, TRAM_L,
  BIKE_C, DOG_C, VIG_C, PUFFS, WARM_PUFFS, GULL_SPR, FTRAM_C;
function renderVignette() {
  const [c, x] = makeCanvas(W, H); const img = x.createImageData(W, H), d = img.data;
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.74, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H / 2) / (H / 2) * 0.9));
    const q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q <= 0) continue;
    const o = (y * W + i) * 4; d[o] = 6; d[o + 1] = 7; d[o + 2] = 18; d[o + 3] = Math.round(q * 0.42 * 255);
  }
  x.putImageData(img, 0, 0);
  return c;
}
function makePuffs(hi, lo) {
  const out = [];
  for (let pr = 1; pr <= 5; pr++) {
    const s = pr * 2 + 1, [c, x] = makeCanvas(s, s);
    for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
      const d = Math.hypot(i - pr, y - pr) / pr;
      if (d < 1 && (1 - d) * 1.6 > bayer(i, y) * 0.9) { x.fillStyle = y < pr * 0.7 ? hi : lo; x.fillRect(i, y, 1, 1); }
    }
    out[pr] = c;
  }
  return out;
}
function renderBike() {
  const p = pix(22, 13);
  for (const cx of [4, 17]) for (let j = -4; j <= 4; j++) for (let i = -4; i <= 4; i++) if (Math.abs(Math.hypot(i, j) - 3.6) < 0.6) p.put(cx + i, 8 + j, j < -2 ? '#8a94b8' : '#0c0d16');
  for (const [a, b, c, d] of [[4, 8, 10, 8], [10, 8, 8, 3], [8, 3, 15, 3], [10, 8, 15, 3], [4, 8, 8, 3], [15, 3, 17, 8], [15, 3, 15, 1]]) line(p, a, b, c, d, '#9a3a34');
  p.rect(7, 2, 3, 1, '#1a1418'); p.rect(15, 0, 2, 1, '#2a2c3a'); p.put(4, 8, '#5a6078'); p.put(17, 8, '#5a6078');
  return p.flush();
}
function renderFarTram() {                            // tram 2 on the Pest quay, far off: yellow, a row of lit windows
  const p = pix(15, 4);
  for (let x = 0; x < 15; x++) { p.put(x, 0, '#c8cad6'); p.put(x, 1, x % 7 === 6 ? '#3a3020' : '#fff0b8'); p.put(x, 2, '#f0be14'); p.put(x, 3, x % 5 === 2 ? '#14151e' : '#3a3424'); }
  return p.flush();
}
function prerender() {
  HL = {
    moon: makeHalo(44, '#aab4e0', 0.2, 1.8), moonCore: makeHalo(13, '#e0e6f8', 0.3, 1.5),
    parl: makeHalo(58, '#ffb850', 0.13, 1.6), dome: makeHalo(36, '#ffc468', 0.16, 1.6), castle: makeHalo(28, '#ffc878', 0.12, 1.5),
    liberty: makeHalo(8, '#fff0c8', 0.4, 1.3), quay: makeHalo(5, '#ffd890', 0.42, 1.2), bell: makeHalo(14, '#ffe2a0', 0.34, 1.4),
    lamp: makeHalo(40, '#ffc070', 0.24, 1.7), lampCore: makeHalo(11, '#fff0c8', 0.5, 1.4), globe: makeHalo(6, '#ffe0a0', 0.4),
    cart: makeHalo(24, '#ffb060', 0.26, 1.6), coal: makeHalo(6, '#ff7a30', 0.5, 1.2), bill: makeHalo(32, '#ffe0a0', 0.15, 1.6),
    billLamp: makeHalo(5, '#fff0c0', 0.5, 1.3), head: makeHalo(12, '#f4f8ff', 0.5, 1.3), tail: makeHalo(5, '#ff4030', 0.5, 1),
    win: makeHalo(26, '#ffe0a0', 0.13, 1.5), led: makeHalo(24, '#a8d0ff', 0.2, 1.5), spark: makeHalo(9, '#bfe0ff', 0.7, 1.2),
    boat: makeHalo(18, '#ffcc80', 0.2), car: makeHalo(3, '#fff0d0', 0.45, 1), carR: makeHalo(3, '#ff4030', 0.4, 1), screen: makeHalo(4, '#a0d8ff', 0.45, 1),
  };
  SKY_C = renderSky(); MOON_C = renderMoon(); CLOUDS = CLOUD_DEFS.map(makeCloudLayer);
  LAND = renderLand(); WATER_C = renderWater();
  CRUISE_C = [true, false].map(g => { const cr = renderCruise(g); return [cr, flipH(cr)]; });   // [with guests, without][heading right, left]
  BARGE_C = renderBarge(); SHIP_C = renderShip();
  FOGS = FOG_DEFS.map(makeFog);
  BRIDGE_C = trim(renderBridge()); CART_C = renderCart();
  BILL_C = renderBillboard(); AD_C = renderAd(); PITCH = makePitch(BILL.w, BILL.h, { accent: '#ffff00' });
  TRAM_C = renderTram(false); TRAM_L = renderTram(true); FTRAM_C = renderFarTram();
  const bike = renderBike(); BIKE_C = [bike, flipH(bike)];
  DOG_C = [0, 1].map(f => [sprite(DOGS[f], DOG_COL, false), sprite(DOGS[f], DOG_COL, true)]);
  PUFFS = makePuffs('#7e86a6', '#5a6284'); WARM_PUFFS = makePuffs('#e8d2b0', '#b89a84');
  GULL_SPR = [{ 1: '#9aa2be', 2: '#c6ccde' }, { 1: '#ffe2a0', 2: '#fff6d8' }].map(pal => FLAP.map(rows => sprite(rows, pal, false)));
  VIG_C = renderVignette();
}

/* ================= Per-frame: sky, river ================= */
function drawScroll(cv, y, k, t) {
  const off = scroll(t, k, W);
  ctx.drawImage(cv, off - W, y); ctx.drawImage(cv, off, y);
}
function drawSky(t) {
  ctx.drawImage(SKY_C, 0, 0);
  ctx.fillStyle = '#c8d2f0';
  for (const s of STARS) { const a = 0.3 + 0.3 * wave(t, s.k, s.p) + s.b * 0.35; if (a > 0.25) { ctx.globalAlpha = Math.min(1, a); ctx.fillRect(s.x, s.y, 1, 1); } }
  ctx.globalAlpha = 1;
  halo(HL.moon, MOON.x, MOON.y, 0.85 + 0.15 * wave(t, 4)); halo(HL.moonCore, MOON.x, MOON.y);
  ctx.globalAlpha = 0.9; ctx.drawImage(MOON_C, MOON.x - 6, MOON.y - 6); ctx.globalAlpha = 1;
  for (const c of CLOUDS) drawScroll(c.c, c.y, c.k, t);
  halo(HL.moon, MOON.x, MOON.y, 0.5);                           // its glow through the cloud
}
function drawRiver(t) {
  ctx.drawImage(WATER_C, 0, RIV0);
  for (let k = 0, n = RIV1 - RIV0; k < n; k++) {                // both banks, mirrored and rippling
    const off = Math.round(Math.sin(TAU * 45 * t / LOOP + k * 0.55) * (0.5 + k * 0.04) + Math.sin(TAU * 110 * t / LOOP - k * 1.3) * 0.7);
    ctx.drawImage(LAND.refl, 0, k, W, 1, off, RIV0 + k, W, 1);
  }
  for (let y = RIV0 + 4, n = 0; y < RIV1; y += 2, n++) for (let j = 0; j < 4; j++) {   // drifting ripple troughs and glints
    const k = 3 + ((n * 7 + j * 5) % 9), len = 5 + Math.floor(hash(n, j, 41) * 16);
    const x = Math.round(frac(hash(n, j, 40) - k * t / LOOP) * (W + 40)) - 20;
    ctx.globalAlpha = j === 3 ? 0.3 : 0.42;
    R(x, y + (j & 1), len, 1, j === 3 ? '#909ac8' : '#060814');
  }
  ctx.globalAlpha = 1;
}
function drawFog(f, t) {
  const D = clamp(f.dens(t), 0, 0.999) * FOG_LEVELS, k = Math.floor(D), fr = D - k;
  let off = scroll(t, f.k, W);
  if (f.dir < 0) off = (W - off) % W;
  for (const [cv, a] of [[f.levels[k], 1 - fr], [f.levels[k + 1], fr]]) {
    if (a * f.alpha < 0.004) continue;
    ctx.globalAlpha = a * f.alpha;
    ctx.drawImage(cv, off - W, f.y); ctx.drawImage(cv, off, f.y);
  }
  ctx.globalAlpha = 1;
}

/* ================= Per-frame: both banks ================= */
const on = (w, t, salt) => hash(w.id + salt, Math.floor(frac(t / LOOP + w.off) * w.n), 3) < w.p;
function bellLevel(t) {
  let b = 0;
  for (const tb of BELLS) { const dt = t - tb; if (dt >= 0 && dt < 3) b = Math.max(b, Math.exp(-dt * 1.3) * (1 - dt / 3)); }
  return b;
}
function drawBillboard(t, pitch) {
  halo(HL.bill, BILL.x + (BILL.w >> 1), BILL.y + 3, 0.9 + 0.1 * wave(t, 8, 0.2));
  blit(BILL_C);
  if (!PITCH.covers(t, pitch)) {
    ctx.drawImage(AD_C, BILL.x, BILL.y);
    for (const [ox, ph] of AD_STEAM) for (let j = 1; j <= 5; j++) {                 // steam off the pool
      const y = 14 - j, sway = Math.sin(y * 1.1 + TAU * (48 * t / LOOP + ph)) * Math.min(1.6, j * 0.45);
      ctx.globalAlpha = 0.75 - j * 0.11; R(BILL.x + ox + Math.round(sway), BILL.y + y, 1, 1, j < 3 ? '#c6f2ea' : '#9cc4d2');
    }
    ctx.globalAlpha = 1;
  }
  PITCH.draw(ctx, t, BILL.x, BILL.y, pitch);
  BILL_LAMPS.forEach((lx, i) => halo(HL.billLamp, lx, BILL_LAMP_Y + 1, 0.9 + 0.1 * wave(t, 700 + i * 37)));
}
function drawLand(t, billboard, pitch) {
  halo(HL.castle, PALACE.dome, 134, 1); halo(HL.castle, MATTHIAS.x, 128, 0.95); halo(HL.castle, 348, 146, 0.6);
  halo(HL.parl, 112, 142, 0.7); halo(HL.parl, 172, 134, 1); halo(HL.parl, 232, 136, 1); halo(HL.parl, 278, 142, 0.7);
  halo(HL.dome, DOME_X, 100, 0.9 + 0.1 * wave(t, 5));
  blit(LAND.c);
  if (billboard) drawBillboard(t, pitch);
  for (const w of LAND.wins) if (on(w, t, 3)) R(w.x, w.y, w.w || 1, w.h || 1, w.col);
  for (const w of LAND.parlWins) if (on(w, t, 7)) { R(w.x, w.y, w.w, w.h, LIT_B); R(w.x, w.y, w.w, Math.max(1, w.h >> 1), LIT_A); }
  for (let i = 0; i < 5; i++) {                                                     // the flag on the north gable
    const dy = Math.round(Math.sin(TAU * 30 * t / LOOP - i * 0.9) * (i / 4) * 1.1);
    for (let j = 0; j < 3; j++) R(FLAG_AT.x + 1 + i, FLAG_AT.y + j + dy, 1, 1, FLAG_ROWS[j]);
  }
  for (let x = CHAIN.x0; x <= CHAIN.x1; x++) {                                      // the Chain Bridge's bulbs
    const cy = chainCurve(x); if (cy === null) continue;
    R(x, Math.round(cy), 1, 1, hash(x, step(t, 80), 21) < 0.14 ? '#d8b878' : '#fff4c8');
  }
  for (const tx of CHAIN.t) halo(HL.dome, tx, 146, 0.5);
  halo(HL.liberty, LIBERTY.x, LIBERTY.y + 3, 0.85 + 0.15 * wave(t, 9));
  const b = bellLevel(t);
  if (b > 0.02) { ctx.globalAlpha = b; R(MATTHIAS.x - 1, 121, 2, 3, '#ffd27a'); ctx.globalAlpha = 1; halo(HL.bell, MATTHIAS.x, 122, b); }
  for (const tb of BELLS) {                                                         // faint rings of sound from its belfry
    const dt = t - tb;
    if (dt < 0 || dt > 2.4) continue;
    const rr = 4 + dt * 13, n = Math.floor(rr * 2.4);
    ctx.fillStyle = '#ffe2a0'; ctx.globalAlpha = (1 - dt / 2.4) * 0.4;
    for (let j = 0; j < n; j++) { const a = j / n * TAU; ctx.fillRect(Math.round(MATTHIAS.x + Math.cos(a) * rr), Math.round(122 + Math.sin(a) * rr * 0.8), 1, 1); }
    ctx.globalAlpha = 1;
  }
  [...PEST_LAMPS, ...BUDA_LAMPS].forEach((lx, i) => halo(HL.quay, lx, ROADY[lx] - 4, 0.8 + 0.2 * wave(t, 300 + i * 23, i * 0.13)));
  // smoke from the Pest block's chimney
  for (const s of SMOKE) {
    const q = t * s.n / LOOP + s.ph, u = q - Math.floor(q);
    const x = CHIM.x + u * u * 30 + u * 6 + wave(t, s.k, s.p) * 2 * u, y = CHIM.y - 1 - u * 30 + u * u * 8, rr = Math.min(5, 1 + Math.floor(u * 5));
    ctx.globalAlpha = 0.42 * Math.min(1, u * 7) * Math.pow(1 - u, 1.3);
    ctx.drawImage(PUFFS[rr], Math.round(x) - rr, Math.round(y) - rr);
  }
  ctx.globalAlpha = 1;
  // traffic on the quay roads: head lamps one way, tail lamps the other
  for (const c of CARS) {
    const { u } = cycle(t, c.n, c.ph), x = Math.round(c.dir > 0 ? lerp(c.x0, c.x1, u) : lerp(c.x1, c.x0, u));
    if (x < 0 || x >= W) continue;
    const y = ROADY[x] - 1, a = Math.min(1, u * 12, (1 - u) * 12);
    ctx.globalAlpha = a;
    if (c.big) { R(x - 2, y - 1, 5, 2, '#1c1e2c'); R(x - 1, y - 2, 3, 1, '#2c3046'); }
    R(x + (c.dir > 0 ? (c.big ? 2 : 1) : (c.big ? -2 : -1)), y, 1, 1, '#fff6dc'); R(x + (c.dir > 0 ? (c.big ? -2 : -1) : (c.big ? 2 : 1)), y, 1, 1, '#ff4030');
    ctx.globalAlpha = 1;
    halo(c.dir > 0 === (x > 0) ? HL.car : HL.car, x + c.dir, y, 0.7 * a);
  }
  // tram 2 on the Pest quay, between the bridgehead and the turn inland before the Parliament
  for (const f of FAR_TRAMS) {
    if (t < f.t0 || t >= f.t1) continue;
    const u = (t - f.t0) / (f.t1 - f.t0), x = Math.round(f.dir > 0 ? lerp(-18, 92, u) : lerp(92, -18, u)), xm = clamp(x + 7, 0, W - 1);
    ctx.globalAlpha = clamp((92 - x) / 12, 0, 1);
    ctx.drawImage(FTRAM_C, x, ROADY[xm] - 4);
    ctx.globalAlpha = 1;
    halo(HL.globe, x + 7, ROADY[xm] - 3, 0.6 * clamp((92 - x) / 12, 0, 1));
  }
}
function drawGulls(t) {
  const wide = 1 + 0.45 * smooth(122, 128, t) * (1 - smooth(140, 156, t));             // the bells send them out a little
  for (const g of GULLS) {
    const a = TAU * (g.k * t / LOOP + g.ph) * g.dir;
    const x = DOME_X + Math.cos(a) * g.rx * wide, y = g.cy + Math.sin(a) * g.ry * wide + 2 * wave(t, g.k2, g.ph);
    const lit = y > 70 && Math.abs(x - DOME_X) < 56 ? 1 : 0;
    const glide = hash(g.i, step(t, 96), 9) < 0.55, fr = glide ? 1 : [0, 1, 2, 1][Math.floor(t * 5 + g.i * 1.7) % 4];
    ctx.drawImage(GULL_SPR[lit][fr], Math.round(x) - 2, Math.round(y) - 1);
  }
}

/* ================= Per-frame: on the river ================= */
function drawRiverLife(t, crowd) {
  for (const s of STREAKS) { const y0 = BANKY[clamp(s.x, 0, W - 1)] + 1; streak(ctx, t, s.x, s.w, s.col, s.a, s.seed, y0, Math.min(RIV1, y0 + s.len)); }
  // the hotel ship at the Pest quay, her lights on the water
  { const S = SHIP_C.c, w = S.c.width, h = S.c.height;
    for (let j = 0; j < 9; j++) {
      const off = Math.round(Math.sin(TAU * 45 * t / LOOP + j * 0.7) * (0.5 + j * 0.14));
      ctx.globalAlpha = 0.3 * (1 - j / 9);
      ctx.drawImage(S.c, 0, h - 3 - j, w, 1, S.x + off, S.y + h + j, w, 1);
    }
    ctx.globalAlpha = 1;
    blit(S);
    for (const wn of SHIP_C.wins) if (!on(wn, t, 5)) R(wn.x, wn.y, wn.w, wn.h, '#39405c');
    for (let x = SHIP.x0 + 9; x < SHIP.x1 - 10; x += 8) streak(ctx, t, x, 3, '#ffd27a', 0.24, 40 + x, BANKY[x] + 4, Math.min(RIV1, BANKY[x] + 24));
  }
  // the barge, coming up the Buda side towards the bridge and under it
  if (t >= BARGE.t0 && t < BARGE.t1) {
    const u = (t - BARGE.t0) / (BARGE.t1 - BARGE.t0), e = Math.pow(u, 1.5), s = lerp(0.3, 1.4, e);
    const w = Math.round(BARGE_W * s), h = Math.round(BARGE_H * s), x = Math.round(lerp(340, 446, e) - w / 2), y = Math.round(lerp(160, 244, e)) - h;
    ctx.globalAlpha = 0.3; R(x + 2, y + h, w - 4, Math.max(1, Math.round(3 * s)), '#05060e'); ctx.globalAlpha = Math.min(1, u * 10);
    ctx.drawImage(BARGE_C, x, y, w, h);
    ctx.globalAlpha = 1;
    halo(HL.globe, x + w * 0.13, y + h * 0.3, 0.7 * Math.min(1, u * 10));
    streak(ctx, t, Math.round(x + w * 0.13), 2, '#ffd88a', 0.3, 51, y + h, Math.min(RIV1, y + h + 16));
  }
  // the sightseeing boat, turning across the river in front of the bridge
  for (const c of CRUISE) {
    if (t < c.t0 || t >= c.t1) continue;
    const u = (t - c.t0) / (c.t1 - c.t0), x = Math.round(c.dir > 0 ? lerp(-CRUISE_W - 4, W + 4, u) : lerp(W + 4, -CRUISE_W - 4, u));
    const y = Math.round(184 + 5 * Math.sin(Math.PI * u) + 0.6 * wave(t, 40)), spr = CRUISE_C[nobody(crowd) ? 1 : 0][c.dir > 0 ? 0 : 1];
    for (let j = 0; j < 9; j++) {                                                     // its lights on the water
      const off = Math.round(Math.sin(TAU * 45 * t / LOOP + j * 0.7) * (0.5 + j * 0.14));
      ctx.globalAlpha = 0.34 * (1 - j / 9);
      ctx.drawImage(spr, 0, CRUISE_H - 2 - j, CRUISE_W, 1, x + off, y + CRUISE_H + j, CRUISE_W, 1);
    }
    ctx.globalAlpha = 1;
    for (let k = 0; k < 16; k++) {                                                    // a soft wake astern
      const wx = c.dir > 0 ? x - k * 2 - (k * k >> 3) : x + CRUISE_W + k * 2 + (k * k >> 3), wy = y + 13 + ((k * 7) % 3) - (k >> 3);
      if (hash(k, step(t, 1200), 17) < 0.62 - k * 0.03) R(wx, wy, 1, 1, k < 5 ? '#c4cce4' : '#7c86aa');
    }
    ctx.drawImage(spr, x, y);
    for (const lx of [16, 32, 46]) streak(ctx, t, x + (c.dir > 0 ? lx : CRUISE_W - lx), 4, '#ffd88a', 0.26, 60 + lx, y + CRUISE_H + 1, Math.min(RIV1, y + CRUISE_H + 22));
    halo(HL.boat, x + CRUISE_W / 2, y + 6, 0.9); halo(HL.boat, x + (c.dir > 0 ? 16 : CRUISE_W - 16), y + 6, 0.6);
  }
}

/* ================= Per-frame: the bridge and its people ================= */
function flick(t, list, id) {
  let v = 1;
  for (const [a, b] of list) if (t >= a && t < b) { const hv = hash(id, Math.floor(t * 12)); v = hv > 0.45 ? 1 : hv > 0.2 ? 0.5 : 0.2; }
  return v;
}
function drawPeople(t, crowd) {
  const DL = [], put = (y, fn) => DL.push([y, fn]), is = k => here(AT[k].rank, crowd);
  const serve = ((t - SERVE_AT) % SERVE_EVERY + SERVE_EVERY) % SERVE_EVERY;
  if (is('he')) {                                                                     // the couple looking at the Parliament
    put(AT.he.y, () => person(AT.he.x, AT.he.y, WHO.he, { aL: POINTS.some(a => t >= a && t < a + 6) ? 1 : 0 }));
    put(AT.she.y, () => person(AT.she.x, AT.she.y, WHO.she, { lean: frac(t / 40 + 0.2) < 0.45 ? -1 : 0 }));
  }
  if (is('photo')) put(AT.photo.y, () => {                                            // the photographer and his tripod
    const crouch = frac((t - 10) / 40) < 0.2, X = AT.photo.x + 9, Y = AT.photo.y;
    person(AT.photo.x, Y, WHO.photo, { kneel: crouch, aR: crouch ? 3 : 0 });
    for (let k = 0; k <= 12; k++) { R(X - Math.round(k * 0.3), Y - 12 + k, 1, 1, '#0e1018'); R(X + Math.round(k * 0.3), Y - 12 + k, 1, 1, '#1c2030'); }
    R(X, Y - 12, 1, 9, '#0e1018'); R(X - 2, Y - 15, 4, 3, '#14161f'); R(X - 2, Y - 15, 4, 1, '#3d4564'); R(X - 1, Y - 14, 2, 1, crouch ? '#bfe4ff' : '#6a96c8');
    if (crouch) halo(HL.screen, X, Y - 14, 0.8);
  });
  if (is('parent')) {
    put(AT.parent.y, () => person(AT.parent.x, AT.parent.y, WHO.parent, { aR: frac(t / 30 + 0.3) < 0.25 ? 1 : 0 }));
    put(AT.child.y, () => person(AT.child.x, AT.child.y - (hash(7, step(t, 96), 5) < 0.4 ? 1 : 0), WHO.child, {}));
  }
  if (is('friendA')) {                                                                // two friends over paper cups of something hot
    put(AT.friendA.y, () => person(AT.friendA.x, AT.friendA.y, WHO.friendA, { view: 2, dir: 1, cup: true, aR: frac(t / 12) < 0.22 ? 6 : 4 }));
    put(AT.friendB.y, () => person(AT.friendB.x, AT.friendB.y, WHO.friendB, { view: 2, dir: -1, cup: true, aR: frac(t / 12 + 0.5) < 0.22 ? 6 : 4 }));
  }
  if (is('leaner')) put(AT.leaner.y, () => person(AT.leaner.x, AT.leaner.y, WHO.leaner, { aL: 3, aR: 3, lean: hash(11, step(t, 30), 6) < 0.3 ? 1 : 0 }));
  if (is('filmer')) put(AT.filmer.y, () => person(AT.filmer.x, AT.filmer.y, WHO.filmer, { aR: frac(t / 24 + 0.2) < 0.6 ? 2 : 0 }));
  if (is('customer')) put(AT.customer.y, () => person(AT.customer.x, AT.customer.y, WHO.customer, { view: 2, dir: 1, cup: serve >= 2 && serve < 22,
    aR: serve < 3 ? 3 : serve < 22 ? (frac(serve / 4) < 0.4 ? 6 : 4) : 0 }));
  for (const wk of WALKS) {
    if (!here(wk.rank, crowd)) continue;
    for (const [a, b, dir] of wk.pass) {
      if (t < a || t >= b) continue;
      const u = (t - a) / (b - a), x = Math.round(dir > 0 ? lerp(-22, W + 22, u) : lerp(W + 22, -22, u)), fr = Math.floor((t - a) * 3.2) & 1, P = WHO[wk.who];
      put(wk.y, () => {
        if (wk.mate) person(x - dir * 6, wk.y, WHO[wk.mate], { view: 2, dir, step: !fr });
        person(x, wk.y, P, { view: 2, dir, step: fr, aR: wk.bike ? 3 : 0 });
        if (wk.carry) { R(x - dir - 1, wk.y - P.ll - 1, 3, 4, '#b8b0a0'); R(x - dir - 1, wk.y - P.ll - 1, 3, 1, '#e0d8c8'); }
        if (wk.bike) ctx.drawImage(BIKE_C[dir > 0 ? 0 : 1], dir > 0 ? x - 9 : x - 12, wk.y - 11);
        if (wk.dog) {
          const dx = dir > 0 ? x + 9 : x - 17, hx = x + dir * 2, hy = wk.y - P.ll - 1, nx = dir > 0 ? dx + 5 : dx + 2;
          ctx.drawImage(DOG_C[fr][dir > 0 ? 0 : 1], dx, wk.y - 4);
          for (let k = 0; k <= 6; k++) R(Math.round(lerp(hx, nx, k / 6)), Math.round(lerp(hy, wk.y - 3, k / 6)), 1, 1, '#5a4a40');
        }
      });
    }
  }
  DL.sort((p, q) => p[0] - q[0]);
  for (const [, fn] of DL) fn();
}
function drawBridge(t, crowd) {
  blit(BRIDGE_C);
  LAMPS.forEach((X, i) => {
    const li = (0.94 + 0.06 * wave(t, 900 + i * 61, 0.3)) * (i === 1 ? flick(t, LAMP_FLICK, 11) : 1);
    if (li < 0.9) { ctx.globalAlpha = 0.85 * (1 - li); R(X - 2, 161, 4, 5, '#3a2a1c'); R(X - 8, 172, 3, 4, '#3a2a1c'); R(X + 5, 172, 3, 4, '#3a2a1c'); ctx.globalAlpha = 1; }
    halo(HL.lamp, X, LAMP_Y + 6, li); halo(HL.lampCore, X, LAMP_Y, li); halo(HL.globe, X - 7, 174, li); halo(HL.globe, X + 6, 174, li);
  });
  // the chestnut seller behind his cart (stirring the pan, handing over a cone), then the cart, its coals and steam
  const serve = ((t - SERVE_AT) % SERVE_EVERY + SERVE_EVERY) % SERVE_EVERY;
  if (here(AT.vendor.rank, crowd)) person(AT.vendor.x, AT.vendor.y, WHO.vendor, { view: 1, aL: serve < 3 && here(AT.customer.rank, crowd) ? 3 : (step(t, 160) & 1 ? 3 : 4), aR: 0 });
  blit(CART_C);
  for (let k = 0; k < 9; k++) if (hash(k, step(t, 720), 13) < 0.6) R(CART.x + 4 + k, CART.y - 20, 1, 1, hash(k, step(t, 480), 14) < 0.5 ? '#ff9a3a' : '#ffd070');
  halo(HL.coal, CART.x + 8, CART.y - 20, 0.75 + 0.25 * wave(t, 130)); halo(HL.cart, CART.x + 27, CART.y - 22, 0.9 + 0.1 * wave(t, 37));
  for (const s of STEAM) {
    const q = t * s.n / LOOP + s.ph, u = q - Math.floor(q), rr = Math.min(4, 1 + Math.floor(u * 4));
    const x = CART.x + 8 + wave(t, s.k, s.p) * 3 * u - u * 5, y = CART.y - 22 - u * 20;
    ctx.globalAlpha = 0.34 * Math.min(1, u * 6) * (1 - u);
    ctx.drawImage(WARM_PUFFS[rr], Math.round(x) - rr, Math.round(y) - rr);
  }
  ctx.globalAlpha = 1;
  if (!nobody(crowd)) drawPeople(t, crowd);
}
function drawTrams(t, crowd, reducedMotion) {
  for (const tr of TRAMS) {
    if (t < tr.t0 || t >= tr.t1) continue;
    const u = (t - tr.t0) / (tr.t1 - tr.t0), x = Math.round(lerp(W + 8, -TRAM_W - 8, u)), sx0 = Math.max(0, -x), sw = Math.min(TRAM_W, W - x) - sx0;
    if (sw <= 0) continue;
    for (let k = 0; k < 3; k++) { ctx.globalAlpha = 0.13 - k * 0.035; R(x - 16 - k * 16, 259, 16 + (k ? 0 : 2), 9, '#dfe8ff'); }   // head lamps on the rails ahead
    ctx.globalAlpha = 1;
    ctx.drawImage(tr.light ? TRAM_L : TRAM_C, sx0, 0, sw, PANTO + TRAM_H, x + sx0, TRAM_TOP - PANTO, sw, PANTO + TRAM_H);
    if (!nobody(crowd)) for (const s of SEATS) {                                       // the passengers, dark against the saloon lights
      const X = x + s.x;
      if (X < -4 || X > W + 4 || s.rank >= crowd) continue;
      const hy = TRAM_TOP + (s.stand ? 13 : 17);
      R(X - 1, hy, 3, 4, s.hair ? '#2a1c18' : '#3c2c28'); R(X - 2, hy + 4, 5, s.stand ? (s.door ? 21 : 13) : 4, s.col);
    }
    for (let k = 0; k < 6; k++) { const hx = x + 2 + k * MOD + 60; if (hx > -30 && hx < W + 30) halo(tr.light ? HL.led : HL.win, hx, TRAM_TOP + (tr.light ? 14 : 20), 1); }
    halo(HL.head, x + 2, TRAM_TOP + 37, 0.9); halo(HL.tail, x + TRAM_W - 3, TRAM_TOP + 37, 0.9);
    const px0 = x + PANTO_X - 3, sp = Math.max(0, 1 - Math.abs(px0 - (LAMPS[1] + 7)) / 12);   // a spark off the frosted wire as it passes the holder
    if (!tr.light && sp > 0) { R(px0 - 1, WIRE - 1, 2, 1, '#eaf6ff'); if (!reducedMotion) halo(HL.spark, px0, WIRE, sp * sp); }
  }
}

/* ================= Frame compositor (back to front) ================= */
// pitch: the "your ad here" face (promo captures) on the roof sign; billboard: false leaves the sign out (the loop videos);
// weather: a WEATHERS pick (site/js/engine/weather.js), the city's own when left out; crowd: the share of the people to
// show, 0..1 (CROWD when left out or not a number); 0 = nobody at all (see `nobody`)
function renderFrame(t, { pitch, billboard = true, weather, reducedMotion = false, crowd: share } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX, lights = billboard ? LIGHTS_BB : LIGHTS;
  const crowd = clamp(typeof share === 'number' && share === share ? share : CROWD, 0, 1);
  drawSky(t);
  if (wx.snow) drawSnow(SNOW[0], t, lights);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  drawRiver(t);
  drawLand(t, billboard, pitch);
  drawGulls(t);
  drawRiverLife(t, crowd);
  drawFog(FOGS[0], t); drawFog(FOGS[1], t);
  if (wx.snow) drawSnow(SNOW[1], t, lights);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  drawBridge(t, crowd);
  drawTrams(t, crowd, reducedMotion);
  if (wx.snow) drawSnow(SNOW[2], t, lights);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'budapest',
  name: 'Budapest',
  tagline: 'Snow over the Danube',
  timeZone: 'Europe/Budapest',
  accent: '#ffff00',                                   // the yellow of the trams, written out: tools/seo/seo.mjs reads it from the source
  focusX: DOME_X,
  landmark: [142, 248],                                // the Parliament's dome and the towers either side of it
  landmarkTop: Math.round(py3(134, 61.5, 96)) - 1,     // the row of the dome's spire tip
  billboard: BILL,
  crowd: CROWD,                                        // the share of the people shown by default; render(t, { crowd }) thins them, 0 = nobody
  ambience: { wind: 0.2, water: 0.3, traffic: 0.15 },
  events: [
    { t: TRAMS[0].t0, type: 'train', dur: 14, pan: [1, -1], gain: 0.7 },
    { t: 30, type: 'gull', x: DOME_X, gain: 0.35 },
    { t: CRUISE[0].t0 + 2, type: 'boat', dur: 56, pan: [-1, 1], gain: 0.35 },
    { t: TRAMS[1].t0, type: 'train', dur: 14, pan: [1, -1], gain: 0.7 },
    ...BELLS.map(t => ({ t, type: 'bell', x: MATTHIAS.x, gain: 0.5 })),
    { t: TRAMS[2].t0, type: 'chime', x: 360, gain: 0.5 },
    { t: TRAMS[2].t0 + 0.5, type: 'train', dur: 18, pan: [1, -1], gain: 0.45 },
    { t: CRUISE[1].t0 + 2, type: 'boat', dur: 38, pan: [1, -1], gain: 0.35 },
    { t: 200, type: 'gull', x: 230, gain: 0.3 },
  ],
  create(c) {
    ctx = c;
    ctx.imageSmoothingEnabled = false;
    prerender();
    return { render: renderFrame };
  },
};
